import { useEffect, useMemo, useRef, useState } from 'react';
import { FEATURES, isFeatureEnabled } from '../../config/featureFlags';
import type { FeatureKey } from '../../config/featureFlags';
import { AURORA_ENDPOINTS, callAuroraEndpoint, fetchAuroraHealth } from '../api/auroraClient';
import type { AuroraEndpointConfig, AuroraEndpointKey } from '../api/auroraClient';

type ModuleId =
  | 'workpad'
  | 'nr'
  | 'ppt'
  | 'apr'
  | 'documento'
  | 'risco'
  | 'investigacao-acidente'
  | 'ishikawa';

type ChatRole = 'aurora' | 'user';

type ChatMessage = {
  id: string;
  role: ChatRole;
  text: string;
  timestamp: string;
};

type InterviewPrompt = {
  field: string;
  question: string;
  label: string;
};

type BlockLifecycleState =
  | 'rascunho'
  | 'coletando'
  | 'estruturando'
  | 'em validação'
  | 'refinando'
  | 'aprovado operacionalmente';

type WorkpadBlockType =
  | 'text'
  | 'heading'
  | 'list'
  | 'table'
  | 'code'
  | 'checklist'
  | 'spacer'
  | 'technical'
  | 'documental'
  | 'visual'
  | 'training'
  | 'investigative';

type WorkpadBlockMetadata = {
  level?: number;
  label?: string;
  placeholder?: string;
  interactive?: boolean;
  status?: 'pending' | 'in-progress' | 'complete';
  phase?: BlockLifecycleState;
  completion?: number;
  pending?: string[];
  needsConfirmation?: boolean;
  confidence?: number;
  tags?: string[];
  attachments?: string[];
  references?: string[];
  visualNodes?: string[];
  slides?: string[];
  signals?: string[];
  lastUpdate?: string;
  mode?: string;
  cadence?: string;
  endpoint?: string;
  elapsed?: number;
  fileNames?: string[];
  findings?: string[];
  riskScore?: number;
  triage?: 'pendente' | 'em triagem' | 'triado' | 'revisão humana' | 'aprovado';
  decisionNotes?: string[];
  validated?: boolean;
  inconsistent?: boolean;
};

type OperationalTimelineStage = 'coleta' | 'análise' | 'validação' | 'refinamento' | 'consolidação' | 'aprovação';

type FlowNodeType = 'entrada' | 'processo' | 'decisão' | 'entrega' | 'risco' | 'validação';

type FlowNode = {
  id: string;
  label: string;
  type: FlowNodeType;
  x: number;
  y: number;
  completion: number;
  riskScore: number;
  priority: 'alta' | 'média' | 'baixa';
  validated: boolean;
  inconsistent: boolean;
};

type FlowEdge = {
  id: string;
  from: string;
  to: string;
  label?: string;
  critical?: boolean;
};

type OperationalTimelineEvent = {
  id: string;
  stage: OperationalTimelineStage;
  label: string;
  detail: string;
  at: string;
  active: boolean;
};

type BlockDependency = {
  id: string;
  from: string; // block id que cria dependência
  to: string; // block id que é afetado
  type: 'priority' | 'prerequisite' | 'conflict' | 'enhancement';
  weight: number; // 0-100, intensidade da relação
  reason: string; // descrição da dependência
};

type OperationalBottleneck = {
  id: string;
  blockId: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  reason: string;
  detectedAt: string;
  suggestedAction: string;
  affectedBlocks: string[]; // blocos que sofrem impacto
};

type OperationalPattern = {
  id: string;
  name: string;
  description: string;
  frequency: number; // quantas vezes observada
  triggers: string[]; // condições que ativam padrão
  outcomes: string[]; // resultados típicos
  lastObservedAt: string;
};

type OperationalTensionLevel = 'calmo' | 'ativo' | 'crítico' | 'bloqueado';

type OperationalMacroPhase = 
  | 'coleta operacional'
  | 'análise contextual'
  | 'refinamento técnico'
  | 'validação crítica'
  | 'consolidação'
  | 'revisão humana'
  | 'pronto para execução';

type SessionHistoryEntry = {
  moduleId: ModuleId;
  timestamp: number;
  completedBlocksSnapshot: WorkpadBlock[];
  refinementPatterns: OperationalPattern[];
  finalPhase: string;
};

type AdvancedOperationalMemory = {
  activeContext: string;
  currentDocument: string;
  openInvestigation: string;
  pendingNotes: string[];
  priorDecisions: string[];
  refinements: string[];
  currentPhase: string;
  blockDependencies: BlockDependency[];
  detectedBottlenecks: OperationalBottleneck[];
  recognizedPatterns: OperationalPattern[];
  operationalTensionLevel: OperationalTensionLevel;
  macroPhase: OperationalMacroPhase;
  sessionHistory: SessionHistoryEntry[];
  lastAutoConsolidationCheck: number;
  auroraInterventionFrequency: number; // 0-100, how often Aurora should intervene
};

type OperationalMemoryState = {
  activeContext: string;
  currentDocument: string;
  openInvestigation: string;
  pendingNotes: string[];
  priorDecisions: string[];
  refinements: string[];
  currentPhase: string;
  blockDependencies?: BlockDependency[];
  detectedBottlenecks?: OperationalBottleneck[];
  recognizedPatterns?: OperationalPattern[];
  operationalTensionLevel?: OperationalTensionLevel;
  macroPhase?: OperationalMacroPhase;
  sessionHistory?: SessionHistoryEntry[];
  lastAutoConsolidationCheck?: number;
  auroraInterventionFrequency?: number;
};

type WorkpadBlock = {
  id: string;
  type: WorkpadBlockType;
  content: string;
  metadata?: WorkpadBlockMetadata;
};

type ChatDockState = 'floating' | 'docked-left' | 'docked-right' | 'minimized';
type AuroraPresenceState = 'idle' | 'listening' | 'processing' | 'responding';

type AuroraBridgeState = {
  online: boolean;
  statusCode: number;
  latencyMs: number;
  checkedAt: string;
  error?: string;
};

type ModulePersonality = {
  modeName: string;
  posture: string;
  cadence: string;
  depth: string;
  openingGuidance: string;
  completionGuidance: string;
  quickPrompts: string[];
};

const AURORA_MODULES: Array<{ id: ModuleId; icon: string; label: string; desc: string }> = [
  { id: 'workpad', icon: '🧠', label: 'Workpad Operacional', desc: 'Documento vivo + entrevista assistida' },
  { id: 'nr', icon: '📚', label: 'Central Técnica NR', desc: 'Análise contextual + normas integradas' },
  { id: 'ppt', icon: '🎬', label: 'Gerador de Treinamentos', desc: 'Estrutura automática + refinamento' },
  { id: 'apr', icon: '🦺', label: 'APR Assistida', desc: 'Análise preliminar de risco estruturada' },
  { id: 'documento', icon: '📝', label: 'Documentos SST', desc: 'PGR, PCMSO, laudos, relatórios' },
  { id: 'risco', icon: '⚠️', label: 'Investigação de Ocorrências', desc: 'Ishikawa, análise profunda e ação' },
  { id: 'investigacao-acidente', icon: '🕵️', label: 'Investigação de Acidente', desc: '5 Porquês, causa raiz e plano de ação' },
  { id: 'ishikawa', icon: '🧭', label: 'Diagrama Ishikawa', desc: 'Mapa causal 6M com validação operacional' },
];

const ENDPOINT_BY_MODULE: Record<ModuleId, AuroraEndpointKey> = {
  workpad: 'chat',
  nr: 'consultar-nr',
  ppt: 'gerar-treinamento',
  apr: 'gerar-apr',
  documento: 'gerar-documento',
  risco: 'analisar-risco',
  'investigacao-acidente': 'investigar-acidente',
  ishikawa: 'gerar-diagrama',
};

const INTERVIEW_FLOW: Record<ModuleId, InterviewPrompt[]> = {
  workpad: [
    { field: 'objetivo', label: 'Objetivo', question: 'Qual o objetivo principal do que vamos produzir agora?' },
    { field: 'cenario', label: 'Cenário operacional', question: 'Qual o contexto atual? Onde, quem e com que recursos você trabalha?' },
    { field: 'restricoes', label: 'Restrições', question: 'Quais limitações (legais, técnicas, de prazo) devo respeitar?' },
    { field: 'entregavel', label: 'Entregável', question: 'Qual formato final: relatório, checklist, plano, procedimento ou outro?' },
  ],
  nr: [
    { field: 'nr', label: 'Norma', question: 'Qual(is) NR você quer analisar?' },
    { field: 'tema', label: 'Tema específico', question: 'Qual tema dentro da(s) NR? Ex: EPI, altura, máquinas' },
    { field: 'setor', label: 'Setor/Atividade', question: 'Em qual setor/atividade essa análise será aplicada?' },
  ],
  ppt: [
    { field: 'tema', label: 'Tema do treinamento', question: 'Qual é o tema central?' },
    { field: 'publico', label: 'Público-alvo', question: 'Quem participa? Operadores, supervisores, gerência?' },
    { field: 'slides', label: 'Quantidade base', question: 'Quantos slides você quer como estrutura inicial?' },
    { field: 'tom', label: 'Estilo', question: 'Tom desejado: técnico, didático, executivo ou prático?' },
  ],
  apr: [
    { field: 'atividade', label: 'Atividade', question: 'Qual atividade será analisada?' },
    { field: 'local', label: 'Local/Equipamentos', question: 'Onde? Com quais equipamentos ou materiais?' },
    { field: 'riscos', label: 'Riscos já identificados', question: 'Quais riscos críticos você já conhece?' },
  ],
  documento: [
    { field: 'tipo', label: 'Tipo de documento', question: 'Qual tipo: PGR, PCMSO, laudo, CAT, relatório, outro?' },
    { field: 'empresa', label: 'Empresa/Unidade', question: 'Qual empresa ou filial deve constar?' },
    { field: 'setor', label: 'Setor/Departamento', question: 'Qual(is) setor(es) serão cobertos?' },
  ],
  risco: [
    { field: 'problema', label: 'Ocorrência/Problema', question: 'O que aconteceu ou pode acontecer?' },
    { field: 'metodo', label: 'Método de análise', question: 'Qual método: Ishikawa, 5 Porquês, árvore de falhas, outro?' },
    { field: 'impacto', label: 'Impacto operacional', question: 'Qual foi ou poderia ser o impacto?' },
  ],
  'investigacao-acidente': [
    { field: 'acidente', label: 'Descrição do acidente', question: 'Qual acidente/incidente vamos investigar?' },
    { field: 'setor', label: 'Setor', question: 'Em qual setor ocorreu a ocorrência?' },
    { field: 'impacto', label: 'Impacto', question: 'Qual impacto humano, operacional ou legal foi observado?' },
  ],
  ishikawa: [
    { field: 'problema', label: 'Problema central', question: 'Qual problema central o diagrama deve explicar?' },
    { field: 'setor', label: 'Setor/Processo', question: 'Qual setor ou processo está no escopo?' },
    { field: 'metodo', label: 'Recorte de análise', question: 'Quer enfoque 6M completo ou foco em categorias específicas?' },
  ],
};

const FEED_SOURCES = [
  { name: 'Feed SST Geral', url: 'https://tecnologo-seguranca-do-trabalho.webnode.page/rss/all.xml', category: 'Técnico' },
  { name: 'Notícias SST', url: 'https://tecnologo-seguranca-do-trabalho.webnode.page/rss/noticias.xml', category: 'Notícias' },
  { name: 'Legislação Gov', url: 'https://www.gov.br/trabalho-e-emprego/pt-br/search?origem=form&SearchableText=rss', category: 'Legislação' },
];

const RADIO_SCENES = [
  {
    id: 'aurora-prata',
    label: 'Aurora Prata',
    desc: 'Camada sonora analítica — foco e presença',
    hz: 196,
  },
  {
    id: 'verixa-core',
    label: 'Vérixa Core Pulse',
    desc: 'Pulso operacional — produção contínua',
    hz: 220,
  },
  {
    id: 'sentinel-night',
    label: 'Sentinel Night',
    desc: 'Ambiente noturno — revisão e leitura técnica',
    hz: 174,
  },
];

const MODULE_PERSONALITY: Record<ModuleId, ModulePersonality> = {
  workpad: {
    modeName: 'Modo Workpad Operacional',
    posture: 'Condução estruturante',
    cadence: 'iterativa',
    depth: 'estrutural',
    openingGuidance: 'Vou estruturar com você por camadas: contexto, decisão e entrega.',
    completionGuidance: 'Estrutura consolidada. Seguimos para refinamento final e execução integrada.',
    quickPrompts: ['Mapear contexto', 'Definir entregável', 'Refinar critérios', 'Consolidar estrutura'],
  },
  nr: {
    modeName: 'Modo NR Técnico',
    posture: 'Interpretação normativa',
    cadence: 'criteriosa',
    depth: 'normativa',
    openingGuidance: 'Ativando leitura técnica de norma com foco em aplicação prática no cenário real.',
    completionGuidance: 'Análise normativa consolidada com aplicação contextual pronta para validação operacional.',
    quickPrompts: ['Interpretar requisito NR', 'Contextualizar setor', 'Mapear conformidade', 'Apontar lacunas técnicas'],
  },
  apr: {
    modeName: 'Modo APR Preventivo',
    posture: 'Prevenção orientada a risco',
    cadence: 'progressiva',
    depth: 'preventiva',
    openingGuidance: 'Vou conduzir a APR por risco, causa, barreira e validação de mitigação.',
    completionGuidance: 'APR estruturada com riscos críticos, controles e validação de execução em campo.',
    quickPrompts: ['Mapear atividade crítica', 'Detalhar risco', 'Propor mitigação', 'Validar barreiras'],
  },
  ppt: {
    modeName: 'Modo Treinamento Inteligente',
    posture: 'Construção educacional',
    cadence: 'guiada',
    depth: 'didática',
    openingGuidance: 'Vou estruturar o treinamento por objetivo pedagógico, sequência e retenção.',
    completionGuidance: 'Roteiro de treinamento consolidado com progressão didática e refinamento aplicável.',
    quickPrompts: ['Definir objetivo pedagógico', 'Estruturar sequência', 'Ajustar linguagem', 'Refinar mensagem-chave'],
  },
  documento: {
    modeName: 'Modo Documental Formal',
    posture: 'Formalização técnica',
    cadence: 'metódica',
    depth: 'documental',
    openingGuidance: 'Vou organizar a coleta para gerar documento formal com consistência e rastreabilidade.',
    completionGuidance: 'Documento estruturado com base contextual pronta para fechamento e revisão final.',
    quickPrompts: ['Definir tipo documental', 'Organizar escopo', 'Completar campos formais', 'Validar consistência'],
  },
  risco: {
    modeName: 'Modo Investigação Analítica',
    posture: 'Correlação operacional',
    cadence: 'investigativa',
    depth: 'analítica',
    openingGuidance: 'Vou investigar por evidência, correlação causal e impacto operacional.',
    completionGuidance: 'Investigação consolidada com correlação de causas e decisão operacional orientada.',
    quickPrompts: ['Descrever ocorrência', 'Correlacionar causas', 'Projetar impacto', 'Definir decisão orientada'],
  },
  'investigacao-acidente': {
    modeName: 'Modo Investigação de Acidente',
    posture: 'Análise causal profunda',
    cadence: 'investigativa',
    depth: 'causal',
    openingGuidance: 'Vou estruturar a investigação por causa raiz, impacto e ação corretiva.',
    completionGuidance: 'Investigação concluída com causa raiz e plano de ação priorizado.',
    quickPrompts: ['Descrever acidente', 'Mapear causa raiz', 'Listar ações corretivas', 'Registrar obrigações legais'],
  },
  ishikawa: {
    modeName: 'Modo Ishikawa 6M',
    posture: 'Estruturação visual causal',
    cadence: 'metódica',
    depth: 'estrutural',
    openingGuidance: 'Vou decompor o problema em categorias 6M com rastreabilidade causal.',
    completionGuidance: 'Diagrama Ishikawa consolidado com causas principais e validação operacional.',
    quickPrompts: ['Definir problema central', 'Separar por 6M', 'Priorizar causas', 'Definir plano de mitigação'],
  },
};

const MODULE_FLAG_BY_ID: Partial<Record<ModuleId, FeatureKey>> = {
  ishikawa: 'ISHIKAWA',
  'investigacao-acidente': 'INVESTIGACAO_ACIDENTE',
};

function findEndpointByKey(key: AuroraEndpointKey): AuroraEndpointConfig | undefined {
  return AURORA_ENDPOINTS.find((entry) => entry.key === key);
}

function nowLabel(): string {
  return new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

function phaseFromProgress(completion: number, hasAnyInput: boolean, validating: boolean, approved: boolean): BlockLifecycleState {
  if (approved && completion >= 90) return 'aprovado operacionalmente';
  if (validating) return 'em validação';
  if (completion >= 78) return 'refinando';
  if (completion >= 42) return 'estruturando';
  if (hasAnyInput) return 'coletando';
  return 'rascunho';
}

function clampNumber(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function safeReadStorage(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeWriteStorage(key: string, value: string): void {
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    // Sessão indisponível, segue sem persistência local.
  }
}

function createTimelineEvents(memory: OperationalMemoryState): OperationalTimelineEvent[] {
  const timelineBase: Array<{ stage: OperationalTimelineStage; label: string; detail: string }> = [
    { stage: 'coleta', label: 'Coleta', detail: memory.activeContext || 'Contexto inicial em construção' },
    { stage: 'análise', label: 'Análise', detail: memory.pendingNotes[0] || 'Sem pendência crítica registrada' },
    { stage: 'validação', label: 'Validação', detail: memory.priorDecisions[0] || 'Aguardando confirmação humana' },
    { stage: 'refinamento', label: 'Refinamento', detail: memory.refinements[0] || 'Refinamento contínuo em andamento' },
    { stage: 'consolidação', label: 'Consolidação', detail: memory.currentDocument || 'Documento em consolidação' },
    { stage: 'aprovação', label: 'Aprovação', detail: memory.openInvestigation || 'Sem revisão pendente' },
  ];

  const activeIndex = Math.max(
    0,
    timelineBase.findIndex((entry) => entry.detail && entry.detail !== 'Sem pendência crítica registrada' && entry.detail !== 'Aguardando confirmação humana' && entry.detail !== 'Refinamento contínuo em andamento' && entry.detail !== 'Documento em consolidação' && entry.detail !== 'Sem revisão pendente'),
  );

  return timelineBase.map((entry, index) => ({
    id: entry.stage,
    stage: entry.stage,
    label: entry.label,
    detail: entry.detail,
    at: nowLabel(),
    active: index <= activeIndex + 1,
  }));
}

function parseOperationalFile(file: File): Promise<{ summary: string; lines: string[]; signals: string[]; riskScore: number }> {
  const fileName = file.name.toLowerCase();
  const textTypes = ['text/', 'application/json', 'application/xml', 'application/xhtml+xml', 'application/csv'];
  const isTextLike = textTypes.some((type) => file.type.startsWith(type)) || /\.(txt|md|csv|json|xml|log|ini|html?|rtf)$/i.test(fileName);

  const analyzeText = (content: string) => {
    const normalized = content.replace(/\s+/g, ' ').trim();
    const lines = content
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean)
      .slice(0, 24);
    const signals: string[] = [];
    if (/assinatura|assinado|rubrica/i.test(content)) signals.push('assinatura ou validação documental detectada');
    if (/pendente|faltando|ausente|não consta|nao consta/i.test(content)) signals.push('indício de pendência ou lacuna registrada');
    if (/inconsist|diverg|erro|anomali|fraude|suspeit/i.test(content)) signals.push('sinal de inconsistência operacional');
    if (/data|hora|prazo/i.test(content)) signals.push('marcação temporal identificada');
    const riskScore = clampNumber(35 + signals.length * 18 + Math.min(20, Math.floor(normalized.length / 600)), 5, 95);
    return {
      summary: normalized.slice(0, 260) || 'Arquivo textual sem conteúdo legível suficiente.',
      lines,
      signals: signals.length ? signals : ['sem sinais críticos imediatos'],
      riskScore,
    };
  };

  if (isTextLike) {
    return file.text().then(analyzeText);
  }

  return Promise.resolve({
    summary: `Arquivo binário ou multimídia recebido: ${file.name} (${file.type || 'tipo não informado'})`,
    lines: [`Arquivo: ${file.name}`, `Tipo: ${file.type || 'desconhecido'}`, `Tamanho: ${Math.max(1, Math.round(file.size / 1024))} KB`],
    signals: ['inspeção contextual por metadados', 'conteúdo visual/multimídia aguardando revisão'],
    riskScore: clampNumber(22 + Math.floor(file.size / 100000), 10, 70),
  });
}

function seedWorkpadBlocks(module: ModuleId): WorkpadBlock[] {
  const baseBlocks: WorkpadBlock[] = [
    {
      id: 'header-module',
      type: 'heading',
      content: `Operação: ${module.toUpperCase()}`,
      metadata: { level: 1 },
    },
    {
      id: 'section-context',
      type: 'heading',
      content: 'CONTEXTO E COLETA DE FATOS',
      metadata: { level: 2 },
    },
    {
      id: 'field-objetivo',
      type: 'text',
      content: '',
      metadata: { label: 'Objetivo', placeholder: 'A Aurora preencherá automaticamente...' },
    },
    {
      id: 'field-cenario',
      type: 'text',
      content: '',
      metadata: { label: 'Cenário Operacional', placeholder: '' },
    },
    {
      id: 'spacer-1',
      type: 'spacer',
      content: '',
    },
    {
      id: 'section-analysis',
      type: 'heading',
      content: 'ESTRUTURA GUIADA PELA AURORA',
      metadata: { level: 2 },
    },
    {
      id: 'checklist-main',
      type: 'checklist',
      content: 'Coleta progressiva|Revisão contextual|Validação de critérios|Antecipação de risco',
      metadata: { interactive: true },
    },
    {
      id: 'spacer-2',
      type: 'spacer',
      content: '',
    },
    {
      id: 'section-output',
      type: 'heading',
      content: 'GERAÇÃO E REFINAMENTO',
      metadata: { level: 2 },
    },
    {
      id: 'status-output',
      type: 'text',
      content: 'Aguardando entrada da entrevista...',
      metadata: { status: 'pending' },
    },
    {
      id: 'block-technical',
      type: 'technical',
      content: 'Checklist técnico e critérios de conformidade serão gerados progressivamente.',
      metadata: {
        phase: 'rascunho',
        completion: 6,
        pending: ['Coleta inicial do cenário', 'Validação de risco crítico'],
        needsConfirmation: true,
        confidence: 36,
        tags: ['checklist operacional', 'conformidade', 'riscos', 'validações'],
      },
    },
    {
      id: 'block-documental',
      type: 'documental',
      content: 'Estrutura documental viva será preenchida com base no contexto coletado.',
      metadata: {
        phase: 'rascunho',
        completion: 8,
        pending: ['Definir escopo formal', 'Confirmar anexos obrigatórios'],
        needsConfirmation: true,
        confidence: 34,
        attachments: ['anexo-operacional.pdf (pendente)', 'evidencias-campo.jpg (pendente)'],
        references: ['NR-01', 'NR-12'],
      },
    },
    {
      id: 'block-visual',
      type: 'visual',
      content: 'Fluxos e prévias visuais serão estruturados para apoiar a decisão operacional.',
      metadata: {
        phase: 'rascunho',
        completion: 5,
        pending: ['Definir fluxo crítico', 'Selecionar mídia de apoio'],
        needsConfirmation: true,
        confidence: 28,
        visualNodes: ['Entrada', 'Processamento', 'Validação', 'Entrega'],
      },
    },
    {
      id: 'block-training',
      type: 'training',
      content: 'Narrativa de treinamento será criada com progressão didática e tópicos vivos.',
      metadata: {
        phase: 'rascunho',
        completion: 7,
        pending: ['Definir objetivo pedagógico', 'Ajustar narrativa do público-alvo'],
        needsConfirmation: true,
        confidence: 32,
        slides: ['Abertura', 'Risco central', 'Procedimento seguro', 'Fechamento'],
      },
    },
    {
      id: 'block-investigative',
      type: 'investigative',
      content: 'Camada investigativa irá correlacionar causa, evidência e mitigação com sinais de suspeita.',
      metadata: {
        phase: 'rascunho',
        completion: 4,
        pending: ['Registrar evidência-base', 'Definir hipótese principal'],
        needsConfirmation: true,
        confidence: 30,
        signals: ['pista documental', 'lacuna de processo', 'brecha de controle'],
      },
    },
  ];

  return baseBlocks;
}

function seedFlowNodes(
  interviewData: Record<string, string>,
  interviewFlow: Array<{ field: string; label: string; question: string }>,
  memory: OperationalMemoryState,
  completion: number,
): FlowNode[] {
  const labels = [
    interviewData.objetivo || interviewData.problema || interviewData.tema || 'Contexto Principal',
    interviewData.cenario || interviewData.setor || interviewData.local || 'Cenário Operacional',
    interviewData.restricoes || interviewData.riscos || interviewData.metodo || 'Análise de Risco',
    interviewData.entregavel || interviewData.tipo || interviewData.tom || 'Entregável Final',
    memory.pendingNotes[0] || interviewFlow.find((e) => !interviewData[e.field])?.label || 'Pendência Ativa',
  ];
  const types: FlowNodeType[] = ['entrada', 'processo', 'risco', 'entrega', 'validação'];
  const baseX = [60, 200, 340, 480, 340];
  const baseY = [120, 80, 120, 80, 220];

  return labels.map((label, index) => ({
    id: `fn-${index}`,
    label: label.slice(0, 32),
    type: types[index],
    x: baseX[index],
    y: baseY[index],
    completion: clampNumber(Math.round(completion * (1 - index * 0.06)), 0, 100),
    riskScore: clampNumber(25 + index * 12 + Math.round(completion * 0.18), 5, 95),
    priority: index === 0 ? 'alta' : index === 2 ? 'alta' : 'média',
    validated: completion >= 80 && index < 3,
    inconsistent: completion < 35 && index === 2,
  }));
}

function seedFlowEdges(nodes: FlowNode[]): FlowEdge[] {
  if (nodes.length < 4) return [];
  return [
    { id: 'e-01', from: nodes[0].id, to: nodes[1].id, label: 'contexto → análise', critical: false },
    { id: 'e-12', from: nodes[1].id, to: nodes[2].id, label: 'análise → risco', critical: true },
    { id: 'e-23', from: nodes[2].id, to: nodes[3].id, label: 'risco → entrega', critical: false },
    { id: 'e-24', from: nodes[2].id, to: nodes[4]?.id || nodes[3].id, label: 'risco → validação', critical: true },
    { id: 'e-43', from: nodes[4]?.id || nodes[3].id, to: nodes[3].id, label: 'validação → entrega', critical: false },
  ].filter((e) => e.from && e.to && e.from !== e.to);
}

// ===== ORQUESTRAÇÃO OPERACIONAL =====

function generateBlockDependencies(blocks: WorkpadBlock[], interviewCompletion: number): BlockDependency[] {
  const deps: BlockDependency[] = [];
  const investigativeBlock = blocks.find((b) => b.type === 'investigative');
  const documentalBlock = blocks.find((b) => b.type === 'documental');
  const technicalBlock = blocks.find((b) => b.type === 'technical');
  const visualBlock = blocks.find((b) => b.type === 'visual');

  // Risco elevado no investigativo → aumenta prioridade do documental
  if (investigativeBlock && documentalBlock) {
    const invRiskScore = (investigativeBlock.metadata?.riskScore as number | undefined) || 0;
    if (invRiskScore >= 70) {
      deps.push({
        id: `dep-inv-doc`,
        from: investigativeBlock.id,
        to: documentalBlock.id,
        type: 'priority',
        weight: clampNumber(invRiskScore - 30, 30, 100),
        reason: 'Risco elevado detectado requer validação documental crítica',
      });
    }
  }

  // Bloco técnico incompleto → visual requer prerequisito
  if (technicalBlock && visualBlock) {
    const techCompletion = (technicalBlock.metadata?.completion as number | undefined) || 0;
    if (techCompletion < 50) {
      deps.push({
        id: `dep-tech-vis`,
        from: technicalBlock.id,
        to: visualBlock.id,
        type: 'prerequisite',
        weight: 75,
        reason: 'Estrutura técnica necessária como base para visualização',
      });
    }
  }

  // Coleta baixa → todos blocos requerem coleta preliminar
  if (interviewCompletion < 40) {
    const mainBlock = blocks.find((b) => b.type === 'heading' && b.content?.includes('CONTEXTO'));
    blocks
      .filter((b) => b.type !== 'heading' && b.type !== 'spacer')
      .forEach((b) => {
        if (mainBlock) {
          deps.push({
            id: `dep-coll-${b.id}`,
            from: mainBlock.id,
            to: b.id,
            type: 'prerequisite',
            weight: 40,
            reason: 'Coleta de contexto deve ser parcialmente concluída',
          });
        }
      });
  }

  return deps;
}

function propagateBlockImpact(
  blocks: WorkpadBlock[],
  dependencies: BlockDependency[],
): WorkpadBlock[] {
  // Deep copy para não mutar o estado React original
  const updated = blocks.map((b) => ({
    ...b,
    metadata: b.metadata ? { ...b.metadata, tags: b.metadata.tags ? [...b.metadata.tags] : undefined } : b.metadata,
  }));

  dependencies.forEach((dep) => {
    const fromBlock = updated.find((b) => b.id === dep.from);
    const toBlock = updated.find((b) => b.id === dep.to);

    if (fromBlock?.metadata && toBlock?.metadata) {
      if (dep.type === 'priority') {
        const currentPriority = toBlock.metadata.completion || 0;
        toBlock.metadata.completion = clampNumber(
          currentPriority + Math.floor(dep.weight * 0.15),
          0,
          100
        );
      }

      if (dep.type === 'prerequisite' && fromBlock.metadata.completion !== undefined && fromBlock.metadata.completion < 50) {
        if (!toBlock.metadata.tags) toBlock.metadata.tags = [];
        if (!toBlock.metadata.tags.includes('bloqueado-por-prerequisito')) {
          toBlock.metadata.tags.push('bloqueado-por-prerequisito');
        }
      }
    }
  });

  return updated;
}

function detectBottlenecks(blocks: WorkpadBlock[], interviewCompletion: number, timeActiveMinutes: number): OperationalBottleneck[] {
  const bottlenecks: OperationalBottleneck[] = [];

  blocks.forEach((block) => {
    const completion = (block.metadata?.completion as number | undefined) || 0;
    const pending = (block.metadata?.pending as string[] | undefined) || [];

    // Bloco incompleto há muito tempo
    if (completion < 40 && timeActiveMinutes > 20) {
      bottlenecks.push({
        id: `bn-${block.id}-inactive`,
        blockId: block.id,
        severity: timeActiveMinutes > 40 ? 'critical' : 'high',
        reason: 'Bloco com progresso baixo por tempo prolongado',
        detectedAt: nowLabel(),
        suggestedAction: `Revisar ${block.type} — pode haver bloqueio técnico ou dependência não clara`,
        affectedBlocks: [block.id],
      });
    }

    // Muitos itens pendentes
    if (pending.length > 5) {
      bottlenecks.push({
        id: `bn-${block.id}-many-pending`,
        blockId: block.id,
        severity: 'medium',
        reason: `Muitas pendências acumuladas (${pending.length})`,
        detectedAt: nowLabel(),
        suggestedAction: 'Priorizar ou consolidar itens pendentes — risco de dispersão',
        affectedBlocks: [block.id],
      });
    }

    // Risco alto sem validação
    if ((block.metadata?.riskScore as number | undefined) || 0 > 75 && !block.metadata?.validated) {
      bottlenecks.push({
        id: `bn-${block.id}-high-risk`,
        blockId: block.id,
        severity: 'critical',
        reason: 'Risco crítico detectado sem validação',
        detectedAt: nowLabel(),
        suggestedAction: 'Priorizar validação — escalação possível',
        affectedBlocks: [block.id],
      });
    }
  });

  // Gargalo: nenhuma coleta com entrevista muito avançada
  if (interviewCompletion > 80 && !blocks.some((b) => (b.metadata?.completion || 0) > 60)) {
    bottlenecks.push({
      id: `bn-system-stalled-output`,
      blockId: 'system',
      severity: 'critical',
      reason: 'Entrada coletada, mas blocos estruturados não avançaram proporcionally',
      detectedAt: nowLabel(),
      suggestedAction: 'Possível conflito entre entrada e estrutura — revisar mapeamento',
      affectedBlocks: blocks.map((b) => b.id),
    });
  }

  return bottlenecks;
}

function calculateOperationalTension(
  blocks: WorkpadBlock[],
  bottlenecks: OperationalBottleneck[],
  interviewCompletion: number,
  operationalMemory: OperationalMemoryState,
): OperationalTensionLevel {
  const criticalBottlenecks = bottlenecks.filter((b) => b.severity === 'critical').length;
  const avgCompletion = blocks.length > 0 
    ? blocks.reduce((sum, b) => sum + ((b.metadata?.completion as number | undefined) || 0), 0) / blocks.length
    : 0;

  const isValidating = operationalMemory.currentPhase === 'em validação' || operationalMemory.currentPhase === 'validação';
  const pendingCheckpoints = (operationalMemory.priorDecisions || []).length > 2;

  if (criticalBottlenecks >= 2 || (isValidating && avgCompletion < 30)) {
    return 'bloqueado';
  }
  if (criticalBottlenecks >= 1 || (isValidating && avgCompletion < 60) || pendingCheckpoints) {
    return 'crítico';
  }
  if (interviewCompletion > 50 && avgCompletion > 50) {
    return 'ativo';
  }
  return 'calmo';
}

function suggestAutoConsolidation(
  blocks: WorkpadBlock[],
  operationalMemory: OperationalMemoryState,
  interviewCompletion: number,
  bottlenecks: OperationalBottleneck[],
): { shouldConsolidate: boolean; reason: string; suggestedPhase: OperationalMacroPhase } {
  const avgCompletion = blocks.length > 0
    ? blocks.reduce((sum, b) => sum + ((b.metadata?.completion as number | undefined) || 0), 0) / blocks.length
    : 0;
  const criticalBottlenecks = bottlenecks.filter((b) => b.severity === 'critical').length;

  // Critério 1: Validação concluída, sem gargalos críticos
  if (operationalMemory.currentPhase === 'em validação' && avgCompletion >= 85 && criticalBottlenecks === 0) {
    return {
      shouldConsolidate: true,
      reason: 'Validação completa, sem bloqueios — pronto para consolidação',
      suggestedPhase: 'consolidação',
    };
  }

  // Critério 2: Coleta completa, análise avançada
  if (interviewCompletion >= 90 && avgCompletion >= 75 && operationalMemory.currentPhase === 'análise contextual') {
    return {
      shouldConsolidate: true,
      reason: 'Análise contextual madura — avançar para refinamento técnico',
      suggestedPhase: 'refinamento técnico',
    };
  }

  // Critério 3: Refinamento maduro
  if (avgCompletion >= 90 && operationalMemory.currentPhase === 'refinamento técnico' && criticalBottlenecks === 0) {
    return {
      shouldConsolidate: true,
      reason: 'Refinamento finalizado — pronto para validação crítica',
      suggestedPhase: 'validação crítica',
    };
  }

  return { shouldConsolidate: false, reason: 'Critérios de consolidação não atingidos', suggestedPhase: operationalMemory.currentPhase as OperationalMacroPhase };
}

function generateAuroraAdvancedSuggestion(
  operationalTension: OperationalTensionLevel,
  bottlenecks: OperationalBottleneck[],
  blocks: WorkpadBlock[],
  recognizedPatterns: OperationalPattern[],
): string {
  if (operationalTension === 'bloqueado' && bottlenecks.length > 0) {
    const criticalBn = bottlenecks.find((b) => b.severity === 'critical');
    if (criticalBn) return `Aurora: ⚠️ Gargalo crítico — ${criticalBn.suggestedAction}`;
  }

  if (operationalTension === 'crítico') {
    const matchedPattern = recognizedPatterns.find((p) => p.frequency > 2);
    if (matchedPattern) return `Aurora: Padrão reconhecido — ${matchedPattern.outcomes[0] || 'refinar estrutura'}`;
  }

  const incompleteBlocks = blocks.filter((b) => (b.metadata?.completion || 0) < 50).length;
  if (incompleteBlocks > 3) {
    return 'Aurora: 🔍 Muitos blocos em formação — priorize 2-3 focos antes de expandir';
  }

  const highRiskBlocks = blocks.filter((b) => (b.metadata?.riskScore || 0) > 70).length;
  if (highRiskBlocks > 0) {
    return `Aurora: ⚡ Risco detectado — ${highRiskBlocks} bloco(s) requerem validação crítica`;
  }

  return 'Aurora: operação transcorrendo dentro de parâmetros esperados — continue';
}

// Estilos de fase fora do componente — constante estática, sem recriação a cada render
const phaseStyles: Record<BlockLifecycleState, { text: string; background: string; border: string }> = {
  rascunho: {
    text: '#9aa6cc',
    background: 'rgba(154,166,204,0.13)',
    border: '1px solid rgba(154,166,204,0.28)',
  },
  coletando: {
    text: '#7db8ff',
    background: 'rgba(125,184,255,0.13)',
    border: '1px solid rgba(125,184,255,0.28)',
  },
  estruturando: {
    text: '#d3b872',
    background: 'rgba(211,184,114,0.13)',
    border: '1px solid rgba(211,184,114,0.28)',
  },
  'em validação': {
    text: '#00d4ff',
    background: 'rgba(0,212,255,0.13)',
    border: '1px solid rgba(0,212,255,0.28)',
  },
  refinando: {
    text: '#f3c969',
    background: 'rgba(243,201,105,0.13)',
    border: '1px solid rgba(243,201,105,0.28)',
  },
  'aprovado operacionalmente': {
    text: '#22cc88',
    background: 'rgba(34,204,136,0.13)',
    border: '1px solid rgba(34,204,136,0.28)',
  },
};

export function AuroraTab() {
  const runtimeStorageKey = 'aurora-runtime-state';
  const telemetryStorageKey = 'aurora-telemetry-log';
  const [activeModule, setActiveModule] = useState<ModuleId>('workpad');
  const [selectedEndpoint, setSelectedEndpoint] = useState<AuroraEndpointKey>('chat');
  const [payload, setPayload] = useState<Record<string, unknown>>({});
  const [result, setResult] = useState<unknown>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [elapsedMs, setElapsedMs] = useState(0);

  // Chat e presença Aurora
  const [chatDock, setChatDock] = useState<ChatDockState>('floating');
  const [chatInput, setChatInput] = useState('');
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([
    {
      id: 'msg-init',
      role: 'aurora',
      text: 'Olá. Eu sou a Aurora. Vamos construir algo significativo agora? Qual é o seu objetivo?',
      timestamp: nowLabel(),
    },
  ]);

  // Entrevista e contexto
  const [interviewData, setInterviewData] = useState<Record<string, string>>({});
  const [workpadBlocks, setWorkpadBlocks] = useState<WorkpadBlock[]>(seedWorkpadBlocks('workpad'));
  const [operationalMemory, setOperationalMemory] = useState<OperationalMemoryState>({
    activeContext: '',
    currentDocument: '',
    openInvestigation: '',
    pendingNotes: [],
    priorDecisions: [],
    refinements: [],
    currentPhase: 'coleta',
  });
  const [timelineEvents, setTimelineEvents] = useState<OperationalTimelineEvent[]>(createTimelineEvents({
    activeContext: '',
    currentDocument: '',
    openInvestigation: '',
    pendingNotes: [],
    priorDecisions: [],
    refinements: [],
    currentPhase: 'coleta',
  }));
  const [investigativeFileName, setInvestigativeFileName] = useState('');
  const [humanReviewNote, setHumanReviewNote] = useState('');
  const [flowNodes, setFlowNodes] = useState<FlowNode[]>([]);
  const [flowEdges, setFlowEdges] = useState<FlowEdge[]>([]);
  const [draggingNodeId, setDraggingNodeId] = useState<string | null>(null);
  const [dragNodeOffset, setDragNodeOffset] = useState({ x: 0, y: 0 });
  const flowSvgRef = useRef<SVGSVGElement>(null);

  // Orquestração operacional
  const [blockDependencies, setBlockDependencies] = useState<BlockDependency[]>([]);
  const [detectedBottlenecks, setDetectedBottlenecks] = useState<OperationalBottleneck[]>([]);
  const [recognizedPatterns, setRecognizedPatterns] = useState<OperationalPattern[]>([]);
  const [operationalTensionLevel, setOperationalTensionLevel] = useState<OperationalTensionLevel>('calmo');
  const [macroPhase, setMacroPhase] = useState<OperationalMacroPhase>('coleta operacional');
  const [lastAutoConsolidationCheck, setLastAutoConsolidationCheck] = useState<number>(Date.now());
  const [auroraAdvancedSuggestion, setAuroraAdvancedSuggestion] = useState('');

  // Presença e ambientação
  const [activeScene, setActiveScene] = useState<string>('aurora-prata');
  const [soundEnabled, setSoundEnabled] = useState(false);
  const [showTechnicalPanel, setShowTechnicalPanel] = useState(true);
  const [presenceState, setPresenceState] = useState<AuroraPresenceState>('idle');
  const [isAssistantTyping, setIsAssistantTyping] = useState(false);
  const [ambientMessage, setAmbientMessage] = useState('Pronta para iniciar a operação.');
  const [sessionStartedAt] = useState<number>(() => Date.now());
  const [bridgeState, setBridgeState] = useState<AuroraBridgeState>({
    online: false,
    statusCode: 0,
    latencyMs: 0,
    checkedAt: nowLabel(),
  });
  const [runtimeState, setRuntimeState] = useState<{
    lastEndpoint: AuroraEndpointKey;
    lastStatus: number;
    retries: number;
    lastError: string;
    lastSuccessAt: string;
  }>({
    lastEndpoint: 'chat',
    lastStatus: 0,
    retries: 0,
    lastError: '',
    lastSuccessAt: '',
  });

  // Refs para animações e áudio
  const orbMain = useRef<HTMLDivElement>(null);
  const orbAccent = useRef<HTMLDivElement>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const oscillatorRef = useRef<OscillatorNode | null>(null);
  const gainRef = useRef<GainNode | null>(null);
  const chatScrollRef = useRef<HTMLDivElement>(null);
  const chatWindowRef = useRef<HTMLDivElement>(null);

  // Refs estáveis para leitura em efeitos sem dependência circular
  const workpadBlocksRef = useRef<WorkpadBlock[]>([]);
  const operationalMemoryRef = useRef<OperationalMemoryState>({
    activeContext: '', currentDocument: '', openInvestigation: '',
    pendingNotes: [], priorDecisions: [], refinements: [], currentPhase: 'coleta',
  });
  const recognizedPatternsRef = useRef<OperationalPattern[]>([]);

  const appendTelemetry = (entry: Record<string, unknown>) => {
    try {
      const raw = safeReadStorage(telemetryStorageKey);
      const parsed = raw ? (JSON.parse(raw) as Array<Record<string, unknown>>) : [];
      const next = [{ at: new Date().toISOString(), ...entry }, ...parsed].slice(0, 80);
      safeWriteStorage(telemetryStorageKey, JSON.stringify(next));
    } catch {
      // sem bloqueio de UI se telemetria falhar
    }
  };

  const availableModules = useMemo(
    () =>
      AURORA_MODULES.filter((mod) => {
        const flag = MODULE_FLAG_BY_ID[mod.id];
        return flag ? isFeatureEnabled(flag) : true;
      }),
    [],
  );

  useEffect(() => {
    if (availableModules.some((mod) => mod.id === activeModule)) return;
    const fallbackModule = availableModules[0]?.id || 'workpad';
    if (fallbackModule !== activeModule) {
      setActiveModule(fallbackModule);
    }
  }, [activeModule, availableModules]);

  // Valores computados
  const endpoint = useMemo(() => findEndpointByKey(selectedEndpoint), [selectedEndpoint]);
  const activePersonality = MODULE_PERSONALITY[activeModule];
  const interviewFlow = INTERVIEW_FLOW[activeModule];
  const firstMissingPrompt = useMemo(
    () => interviewFlow.find((entry) => !(interviewData[entry.field] || '').trim()) || null,
    [interviewData, interviewFlow],
  );
  const collectedCount = useMemo(
    () => Object.keys(interviewData).filter((k) => interviewData[k]?.trim()).length,
    [interviewData],
  );
  const interviewCompletion = useMemo(
    () => (interviewFlow.length > 0 ? Math.round((collectedCount / interviewFlow.length) * 100) : 0),
    [collectedCount, interviewFlow.length],
  );
  const [sessionUptimeSec, setSessionUptimeSec] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => {
      setSessionUptimeSec(Math.floor((Date.now() - sessionStartedAt) / 1000));
    }, 1000);
    return () => window.clearInterval(id);
  }, [sessionStartedAt]);
  const sessionUptimeLabel = useMemo(() => {
    const h = Math.floor(sessionUptimeSec / 3600);
    const m = Math.floor((sessionUptimeSec % 3600) / 60).toString().padStart(2, '0');
    const s = (sessionUptimeSec % 60).toString().padStart(2, '0');
    return h > 0 ? `${h}h ${m}m ${s}s` : `${m}:${s}`;
  }, [sessionUptimeSec]);

  const presenceLabelByState: Record<AuroraPresenceState, string> = {
    idle: 'Em espera operacional',
    listening: 'Escutando contexto',
    processing: 'Estruturando análise',
    responding: 'Orientando operação',
  };
  const workpadRefinementState = useMemo(() => {
    if (loading) return 'sintetizando';
    if (result !== null) return 'consolidado';
    if (interviewCompletion >= 75) return 'refinando';
    if (interviewCompletion >= 35) return 'estruturando';
    if (interviewCompletion > 0) return 'coletando';
    return 'inicial';
  }, [interviewCompletion, loading, result]);
  const refinementColor = useMemo(() => {
    if (workpadRefinementState === 'consolidado') return '#22cc88';
    if (workpadRefinementState === 'sintetizando') return '#00d4ff';
    if (workpadRefinementState === 'refinando') return '#f3c969';
    return '#8a96c2';
  }, [workpadRefinementState]);
  const operationalSnapshot = useMemo(() => {
    const filledEntries = interviewFlow.filter((entry) => (interviewData[entry.field] || '').trim());
    const missingEntries = interviewFlow.filter((entry) => !(interviewData[entry.field] || '').trim());
    return {
      understood: filledEntries.slice(0, 2).map((entry) => entry.label),
      needs: missingEntries.slice(0, 2).map((entry) => entry.label),
      structured: Math.max(1, Math.ceil((workpadBlocks.length * interviewCompletion) / 100)),
      refining: workpadRefinementState,
      completion: interviewCompletion,
    };
  }, [interviewCompletion, interviewData, interviewFlow, workpadBlocks.length, workpadRefinementState]);

  const visualTopology = useMemo(() => {
    const prioritySeeds = [
      operationalMemory.activeContext || interviewData.objetivo || interviewData.problema || 'Contexto ativo',
      operationalMemory.currentDocument || interviewData.entregavel || interviewData.tipo || 'Documento em formação',
      operationalMemory.openInvestigation || interviewData.riscos || interviewData.atividade || 'Análise aberta',
      operationalMemory.pendingNotes[0] || firstMissingPrompt?.label || 'Pendência principal',
    ];
    return prioritySeeds.map((seed, index) => ({
      id: `node-${index}`,
      label: seed,
      weight: clampNumber(100 - index * 18 + interviewCompletion / 2, 24, 100),
      active: index === 0 || index === 1,
    }));
  }, [firstMissingPrompt?.label, interviewCompletion, interviewData.atividade, interviewData.entregavel, interviewData.objetivo, interviewData.problema, interviewData.riscos, interviewData.tipo, operationalMemory.activeContext, operationalMemory.currentDocument, operationalMemory.openInvestigation, operationalMemory.pendingNotes]);

  const operationalPriorityLabel = useMemo(() => {
    if (loading) return 'consolidação em curso';
    if (operationalMemory.currentPhase === 'aprovação' || result !== null) return 'prioridade consolidada';
    if (operationalMemory.pendingNotes.length > 0) return 'pendência prioritária';
    if (interviewCompletion >= 75) return 'refino final';
    return 'coleta ativa';
  }, [interviewCompletion, loading, operationalMemory.pendingNotes.length, workpadRefinementState]);

  const auroraPresenceHints = useMemo(
    () => [
      activePersonality.posture,
      operationalMemory.currentPhase,
      operationalPriorityLabel,
    ],
    [activePersonality.posture, operationalMemory.currentPhase, operationalPriorityLabel],
  );

  const activeInvestigativeBlock = useMemo(
    () => workpadBlocks.find((block) => block.type === 'investigative') || null,
    [workpadBlocks],
  );

  const updateOperationalMemory = (nextData: Record<string, string>, nextBlockState: WorkpadBlock[], nextPhase: string) => {
    const nextPending = interviewFlow
      .filter((entry) => !(nextData[entry.field] || '').trim())
      .slice(0, 4)
      .map((entry) => entry.label);
    const nextDocument = nextData.entregavel || nextData.tipo || nextData.tom || nextData.tema || operationalMemory.currentDocument;
    const nextInvestigation = nextData.problema || nextData.riscos || nextData.atividade || operationalMemory.openInvestigation;
    const nextDecisions = [...operationalMemory.priorDecisions];
    if (nextPhase === 'aprovado operacionalmente' || nextPhase === 'aprovado') {
      nextDecisions.unshift(`Aprovação operacional às ${nowLabel()}`);
    }
    const nextRefinements = [...operationalMemory.refinements];
    if (nextPhase === 'refinando' || nextPhase === 'estruturando') {
      nextRefinements.unshift(`Refino ativo em ${activePersonality.modeName}`);
    }

    const memory: OperationalMemoryState = {
      ...operationalMemory,
      activeContext: nextData.objetivo || nextData.cenario || nextData.tipo || operationalMemory.activeContext,
      currentDocument: nextDocument,
      openInvestigation: nextInvestigation,
      pendingNotes: nextPending,
      priorDecisions: nextDecisions.slice(0, 4),
      refinements: nextRefinements.slice(0, 4),
      currentPhase: nextPhase,
    };

    setOperationalMemory(memory);
    setTimelineEvents(createTimelineEvents(memory));
    safeWriteStorage('aurora-operational-memory', JSON.stringify(memory));
    safeWriteStorage('aurora-workpad-blocks', JSON.stringify(nextBlockState));
  };

  // Inicializar módulo
  useEffect(() => {
    setSelectedEndpoint(ENDPOINT_BY_MODULE[activeModule]);
    const nextEndpoint = findEndpointByKey(ENDPOINT_BY_MODULE[activeModule]);
    setPayload(nextEndpoint?.samplePayload || {});
    setInterviewData({});
    setResult(null);
    setError(null);
    setElapsedMs(0);
    const savedMemoryRaw = safeReadStorage('aurora-operational-memory');
    const savedBlocksRaw = safeReadStorage('aurora-workpad-blocks');
    const savedMemory = savedMemoryRaw ? (JSON.parse(savedMemoryRaw) as OperationalMemoryState) : null;
    const savedBlocks = savedBlocksRaw ? (JSON.parse(savedBlocksRaw) as WorkpadBlock[]) : null;
    setOperationalMemory(
      savedMemory || {
        activeContext: '',
        currentDocument: '',
        openInvestigation: '',
        pendingNotes: [],
        priorDecisions: [],
        refinements: [],
        currentPhase: 'coleta',
      },
    );
    setTimelineEvents(
      createTimelineEvents(
        savedMemory || {
          activeContext: '',
          currentDocument: '',
          openInvestigation: '',
          pendingNotes: [],
          priorDecisions: [],
          refinements: [],
          currentPhase: 'coleta',
        },
      ),
    );
    setWorkpadBlocks(savedBlocks && savedBlocks.length ? savedBlocks : seedWorkpadBlocks(activeModule));
    setChatMessages([
      {
        id: `init-${activeModule}`,
        role: 'aurora',
        text: `${MODULE_PERSONALITY[activeModule].modeName} ativo. ${MODULE_PERSONALITY[activeModule].openingGuidance}`,
        timestamp: nowLabel(),
      },
    ]);
    setPresenceState('idle');
    setIsAssistantTyping(false);

    const savedRuntimeRaw = safeReadStorage(runtimeStorageKey);
    if (savedRuntimeRaw) {
      try {
        const savedRuntime = JSON.parse(savedRuntimeRaw) as {
          activeModule?: ModuleId;
          selectedEndpoint?: AuroraEndpointKey;
          payload?: Record<string, unknown>;
          interviewData?: Record<string, string>;
          runtimeState?: typeof runtimeState;
        };
        if (savedRuntime.activeModule === activeModule) {
          if (savedRuntime.selectedEndpoint) setSelectedEndpoint(savedRuntime.selectedEndpoint);
          if (savedRuntime.payload && Object.keys(savedRuntime.payload).length > 0) setPayload(savedRuntime.payload);
          if (savedRuntime.interviewData) setInterviewData(savedRuntime.interviewData);
          if (savedRuntime.runtimeState) setRuntimeState(savedRuntime.runtimeState);
        }
      } catch {
        // ignora runtime salvo inválido
      }
    }
  }, [activeModule]);

  useEffect(() => {
    safeWriteStorage(
      runtimeStorageKey,
      JSON.stringify({
        activeModule,
        selectedEndpoint,
        payload,
        interviewData,
        runtimeState,
      }),
    );
  }, [activeModule, interviewData, payload, runtimeState, selectedEndpoint]);

  useEffect(() => {
    const syncFromStorage = (evt: StorageEvent) => {
      if (evt.key !== 'aurora-workpad-blocks' || !evt.newValue) return;
      try {
        const parsed = JSON.parse(evt.newValue) as WorkpadBlock[];
        if (Array.isArray(parsed) && parsed.length) {
          setWorkpadBlocks(parsed);
        }
      } catch {
        // sincronização best effort
      }
    };
    window.addEventListener('storage', syncFromStorage);
    return () => window.removeEventListener('storage', syncFromStorage);
  }, []);

  useEffect(() => {
    let mounted = true;
    const checkBridge = async () => {
      const snap = await fetchAuroraHealth(9000);
      if (!mounted) return;
      setBridgeState({
        online: snap.online,
        statusCode: snap.statusCode,
        latencyMs: snap.latencyMs,
        checkedAt: nowLabel(),
        error: snap.error || '',
      });
      appendTelemetry({ type: 'health-check', online: snap.online, statusCode: snap.statusCode, latencyMs: snap.latencyMs });
    };
    void checkBridge();
    const intervalId = window.setInterval(() => {
      void checkBridge();
    }, 30000);
    return () => {
      mounted = false;
      window.clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    if (presenceState === 'processing') {
      setAmbientMessage('Conectando fatos, critérios e objetivo em uma estrutura operacional.');
      return;
    }
    if (presenceState === 'listening') {
      setAmbientMessage('Captando sinais do seu cenário para orientar a próxima decisão.');
      return;
    }
    if (presenceState === 'responding') {
      setAmbientMessage('Condução ativa: resposta gerada com foco operacional.');
      return;
    }
    if (firstMissingPrompt) {
      setAmbientMessage(`${activePersonality.posture}. Próximo passo: ${firstMissingPrompt.label}`);
    } else {
      setAmbientMessage(activePersonality.completionGuidance);
    }
  }, [activePersonality, firstMissingPrompt, presenceState]);

  // Animação dos orbs
  useEffect(() => {
    const animate = () => {
      if (orbMain.current && orbAccent.current) {
        const intensity =
          presenceState === 'processing'
            ? 1.25
            : presenceState === 'responding'
              ? 1.1
              : presenceState === 'listening'
                ? 0.95
                : 0.8;
        orbMain.current.style.boxShadow = `
          0 0 ${(20 + Math.random() * 30) * intensity}px rgba(106,61,216,${0.25 + Math.random() * 0.45}),
          inset -2px -2px 8px rgba(0,0,0,0.3)
        `;
        orbAccent.current.style.boxShadow = `
          0 0 ${(24 + Math.random() * 32) * intensity}px rgba(0,212,255,${0.2 + Math.random() * 0.4}),
          inset 2px 2px 8px rgba(0,212,255,0.15)
        `;
      }
    };

    const id = window.setInterval(animate, 1200);
    return () => window.clearInterval(id);
  }, [presenceState]);

  // Auto-scroll chat
  useEffect(() => {
    if (!chatScrollRef.current) return;
    chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
  }, [chatMessages]);

  // Áudio contextual
  useEffect(() => {
    if (!soundEnabled) {
      oscillatorRef.current?.stop();
      oscillatorRef.current = null;
      gainRef.current = null;
      return;
    }

    const scene = RADIO_SCENES.find((item) => item.id === activeScene);
    if (!scene) return;

    const context = audioContextRef.current || new window.AudioContext();
    audioContextRef.current = context;

    const gain = context.createGain();
    gain.gain.value = 0.012;

    const oscillator = context.createOscillator();
    oscillator.type = 'sine';
    oscillator.frequency.value = scene.hz;

    oscillator.connect(gain);
    gain.connect(context.destination);
    oscillator.start();

    oscillatorRef.current = oscillator;
    gainRef.current = gain;

    return () => {
      try {
        oscillator.stop();
      } catch {
        // Já parou
      }
    };
  }, [activeScene, soundEnabled]);

  const handlePayloadChange = (key: string, value: unknown) => {
    setPayload((prev) => ({ ...prev, [key]: value }));
  };

  // Sincronizar fluxograma com contexto entrevista
  useEffect(() => {
    const flow = INTERVIEW_FLOW[activeModule];
    const nodes = seedFlowNodes(interviewData, flow, operationalMemory, interviewCompletion);
    setFlowNodes(nodes);
    setFlowEdges(seedFlowEdges(nodes));
  }, [interviewCompletion, activeModule, operationalMemory.currentPhase]);

  // Sincronizar refs para leitura estável em efeitos sem dependência circular
  useEffect(() => { workpadBlocksRef.current = workpadBlocks; }, [workpadBlocks]);
  useEffect(() => { operationalMemoryRef.current = operationalMemory; }, [operationalMemory]);
  useEffect(() => { recognizedPatternsRef.current = recognizedPatterns; }, [recognizedPatterns]);

  // ORQUESTRAÇÃO OPERACIONAL: Dependências, gargalos, tensão, consolidação
  // NOTA: Lê blocks e memory via refs para evitar dependências circulares que causam loop infinito.
  useEffect(() => {
    const blocks = workpadBlocksRef.current;
    const memory = operationalMemoryRef.current;

    // Gerar dependências entre blocos (IDs determinísticos)
    const deps = generateBlockDependencies(blocks, interviewCompletion);
    setBlockDependencies(deps);

    // Propagar impacto das dependências (deep copy garante reatividade)
    const updatedBlocks = propagateBlockImpact(blocks, deps);

    // Detectar e popular padrões operacionais reconhecidos
    const patterns: OperationalPattern[] = [];
    const highCompletionCount = blocks.filter((b) => (b.metadata?.completion ?? 0) >= 75).length;
    if (highCompletionCount >= 3) {
      patterns.push({
        id: 'pat-high-completion',
        name: 'Densidade operacional alta',
        description: 'Múltiplos blocos com conclusão avançada',
        frequency: highCompletionCount,
        triggers: ['completion >= 75'],
        outcomes: ['Consolidação recomendada'],
        lastObservedAt: nowLabel(),
      });
    }
    if (interviewCompletion >= 60) {
      patterns.push({
        id: 'pat-interview-progress',
        name: 'Entrevista em progresso avançado',
        description: `${interviewCompletion}% dos campos coletados`,
        frequency: 1,
        triggers: ['interviewCompletion >= 60'],
        outcomes: ['Execução de API viável'],
        lastObservedAt: nowLabel(),
      });
    }
    const prevPatterns = recognizedPatternsRef.current;
    const patternsChanged =
      patterns.length !== prevPatterns.length ||
      patterns.some((p, i) => p.id !== prevPatterns[i]?.id);
    if (patternsChanged) {
      setRecognizedPatterns(patterns);
    }

    // Detectar gargalos
    const timeActiveMs = Date.now() - sessionStartedAt;
    const timeActiveMinutes = Math.floor(timeActiveMs / 60000);
    const bottlenecks = detectBottlenecks(updatedBlocks, interviewCompletion, timeActiveMinutes);
    setDetectedBottlenecks(bottlenecks);

    // Calcular tensão operacional
    const tension = calculateOperationalTension(updatedBlocks, bottlenecks, interviewCompletion, memory);
    setOperationalTensionLevel(tension);

    // Verificar auto-consolidação a cada 30 segundos (throttle)
    const now = Date.now();
    if (now - lastAutoConsolidationCheck > 30000) {
      const consolidationCheck = suggestAutoConsolidation(updatedBlocks, memory, interviewCompletion, bottlenecks);
      if (consolidationCheck.shouldConsolidate) {
        setMacroPhase(consolidationCheck.suggestedPhase);
        setOperationalMemory((prev) => {
          const updated = {
            ...prev,
            currentPhase: consolidationCheck.suggestedPhase,
            macroPhase: consolidationCheck.suggestedPhase,
          };
          safeWriteStorage('aurora-operational-memory', JSON.stringify(updated));
          return updated;
        });
      }
      setLastAutoConsolidationCheck(now);
    }

    // Gerar sugestão Aurora avançada contextual
    const suggestion = generateAuroraAdvancedSuggestion(
      tension,
      bottlenecks,
      updatedBlocks,
      recognizedPatternsRef.current,
    );
    setAuroraAdvancedSuggestion(suggestion);

    // Aplicar blocos com impacto propagado apenas se houve mudança real de valor
    const hasChanges = updatedBlocks.some((b, i) => {
      const orig = blocks[i];
      return (
        b.metadata?.completion !== orig?.metadata?.completion ||
        JSON.stringify(b.metadata?.tags) !== JSON.stringify(orig?.metadata?.tags)
      );
    });
    if (hasChanges) {
      setWorkpadBlocks(updatedBlocks);
    }
  }, [
    interviewCompletion,
    sessionStartedAt,
    lastAutoConsolidationCheck,
  ]);

  // Handlers de drag dos nós do fluxograma
  const handleNodeMouseDown = (e: React.MouseEvent<SVGGElement>, nodeId: string) => {
    e.preventDefault();
    const svg = flowSvgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const node = flowNodes.find((n) => n.id === nodeId);
    if (!node) return;
    setDraggingNodeId(nodeId);
    setDragNodeOffset({ x: e.clientX - rect.left - node.x, y: e.clientY - rect.top - node.y });
  };

  const handleFlowMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    if (!draggingNodeId) return;
    const svg = flowSvgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const x = clampNumber(e.clientX - rect.left - dragNodeOffset.x, 30, rect.width - 90);
    const y = clampNumber(e.clientY - rect.top - dragNodeOffset.y, 20, rect.height - 50);
    setFlowNodes((prev) => prev.map((n) => (n.id === draggingNodeId ? { ...n, x, y } : n)));
  };

  const handleFlowMouseUp = () => setDraggingNodeId(null);

  const syncWorkpadFromInterview = (nextData: Record<string, string>) => {
    const nextMissing = interviewFlow.filter((entry) => !(nextData[entry.field] || '').trim());
    const nextCollected = interviewFlow.length - nextMissing.length;
    const completion = interviewFlow.length > 0 ? Math.round((nextCollected / interviewFlow.length) * 100) : 0;
    const hasAnyInput = nextCollected > 0;
    const phase = phaseFromProgress(completion, hasAnyInput, false, result !== null);
    const pendingLabels = nextMissing.slice(0, 3).map((entry) => entry.label);
    const confidenceBase = 28 + completion * 0.62;
    const confidence = clampNumber(Math.round(confidenceBase + (result !== null ? 10 : 0)), 15, 97);
    const commonMeta: WorkpadBlockMetadata = {
      phase,
      completion,
      pending: pendingLabels.length ? pendingLabels : ['Sem pendências críticas'],
      needsConfirmation: completion < 92,
      confidence,
      lastUpdate: nowLabel(),
    };

    const updatedBlocks = workpadBlocks.map((block) => {
      if (block.id === 'field-objetivo') {
        return { ...block, content: nextData.objetivo || nextData.tema || nextData.problema || '' };
      }
      if (block.id === 'field-cenario') {
        return { ...block, content: nextData.cenario || nextData.setor || nextData.local || '' };
      }
      if (block.id === 'status-output') {
        const collectedFields = Object.keys(nextData).filter((k) => nextData[k]?.trim());
        const isComplete = collectedFields.length === interviewFlow.length;
        const status: WorkpadBlockMetadata['status'] = isComplete ? 'complete' : 'in-progress';
        return {
          ...block,
          content: isComplete
            ? `${activePersonality.completionGuidance}`
            : `${collectedFields.length}/${interviewFlow.length} campos coletados em ${activePersonality.modeName}. Próxima pergunta: ${nextMissing[0]?.question || 'Pronto para gerar!'}`,
          metadata: {
            status,
            mode: activePersonality.modeName,
            cadence: activePersonality.cadence,
            ...commonMeta,
          },
        };
      }
      if (block.id === 'block-technical') {
        return {
          ...block,
          content:
            completion >= 55
              ? `Checklist operacional em ${phase}. Conformidade e risco sendo cruzados com ${activePersonality.modeName}.`
              : 'Coletando critérios técnicos para checklist, conformidade e validações críticas.',
          metadata: {
            ...block.metadata,
            ...commonMeta,
            tags: ['checklist operacional', 'conformidade', 'riscos', 'status técnico'],
          },
        };
      }
      if (block.id === 'block-documental') {
        return {
          ...block,
          content:
            completion >= 45
              ? `Estrutura documental ativa com seções formais e preenchimento contextual orientado por ${activePersonality.modeName}.`
              : 'Rascunho documental preparado para receber seções formais e anexos.',
          metadata: {
            ...block.metadata,
            ...commonMeta,
            attachments:
              completion >= 68
                ? ['anexo-operacional.pdf (pré-validado)', 'evidencias-campo.jpg (coletada)']
                : ['anexo-operacional.pdf (pendente)', 'evidencias-campo.jpg (pendente)'],
            references: ['NR-01', 'NR-06', 'NR-12'],
          },
        };
      }
      if (block.id === 'block-visual') {
        return {
          ...block,
          content:
            completion >= 40
              ? 'Fluxo visual progressivo com prévia operacional em construção e pontos de decisão destacados.'
              : 'Prévia visual aguardando definição de fluxo crítico.',
          metadata: {
            ...block.metadata,
            ...commonMeta,
            visualNodes:
              completion >= 72
                ? ['Entrada validada', 'Análise contextual', 'Confirmação operacional', 'Entrega estruturada']
                : ['Entrada', 'Processamento', 'Validação', 'Entrega'],
          },
        };
      }
      if (block.id === 'block-training') {
        return {
          ...block,
          content:
            completion >= 50
              ? 'Slides vivos estruturados com narrativa didática progressiva e orientação por objetivo.'
              : 'Estrutura inicial de treinamento aguardando objetivos e público-alvo.',
          metadata: {
            ...block.metadata,
            ...commonMeta,
            slides:
              completion >= 70
                ? ['Contexto', 'Risco e impacto', 'Procedimento orientado', 'Simulação', 'Conclusão']
                : ['Abertura', 'Risco central', 'Procedimento seguro', 'Fechamento'],
          },
        };
      }
      if (block.id === 'block-investigative') {
        const fileName = investigativeFileName || nextData.tipo || nextData.problema || 'documento contextual';
        const triageState: NonNullable<WorkpadBlockMetadata['triage']> = completion >= 86 ? 'aprovado' : completion >= 66 ? 'revisão humana' : completion >= 40 ? 'triado' : completion >= 18 ? 'em triagem' : 'pendente';
        const riskScore = clampNumber(Math.round(20 + completion * 0.78 + (result !== null ? 8 : 0)), 10, 98);
        const findings = completion >= 42 ? ['lacunas documentais avaliadas', 'coerência temporal revisada'] : ['ingestão aguardando maior densidade contextual'];
        return {
          ...block,
          content:
            completion >= 48
              ? `Investigação ativa sobre ${fileName}. Triagem contextual e validação de inconsistências em andamento.`
              : `Camada investigativa pronta para ingestão contextual de ${fileName}.`,
          metadata: {
            ...block.metadata,
            ...commonMeta,
            triage: triageState,
            fileNames: fileName ? [fileName] : [],
            riskScore,
            findings,
            decisionNotes:
              triageState === 'revisão humana' || triageState === 'aprovado'
                ? ['Checkpoint de revisão humana requerido', 'Justificativa técnica registrada no fluxo']
                : ['Triagem automática em progresso'],
            signals:
              completion >= 78
                ? ['inconsistência temporal', 'brecha de autorização', 'assinatura divergente']
                : ['pista documental', 'lacuna de processo', 'brecha de controle'],
          },
        };
      }
      return block;
    });
    setWorkpadBlocks(updatedBlocks);
    updateOperationalMemory(nextData, updatedBlocks, phase);
  };

  const inferAuroraResponse = (prompt: InterviewPrompt | null, userText: string, nextData: Record<string, string>) => {
    const profile = MODULE_PERSONALITY[activeModule];
    if (!prompt) {
      const allFilled = interviewFlow.every((p) => (interviewData[p.field] || '').trim());
      if (allFilled) {
        return `${profile.modeName}: ${profile.completionGuidance}`;
      }
      return `${profile.modeName}: contexto registrado. Seguimos na cadência ${profile.cadence}.`;
    }

    const remaining = interviewFlow.filter((item) => item.field !== prompt.field && !(nextData[item.field] || '').trim());
    if (remaining.length === 0) {
      return `${profile.modeName}: última entrada recebida. Estrutura ${profile.depth} concluída e pronta para execução.`;
    }

    return `${profile.modeName} | ${profile.posture}. Entrada validada: "${prompt.label}". Próximo passo: ${remaining[0].question}`;
  };

  const handleSendChat = async () => {
    const text = chatInput.trim();
    if (!text) return;

    setPresenceState('listening');

    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      text,
      timestamp: nowLabel(),
    };

    const prompt = firstMissingPrompt;
    const nextData = { ...interviewData };
    if (prompt) {
      nextData[prompt.field] = text;
    }

    setPresenceState('processing');
    setIsAssistantTyping(true);

    // Sincronizar payload com dados coletados
    const endpointConfig = findEndpointByKey(ENDPOINT_BY_MODULE[activeModule]);
    if (endpointConfig) {
      const updatedPayload: Record<string, unknown> = { ...endpointConfig.samplePayload };
      Object.keys(updatedPayload).forEach((fieldKey) => {
        const value = nextData[fieldKey] || nextData.tema || nextData.problema || nextData.objetivo || text;
        if (value) updatedPayload[fieldKey] = value;
      });
      setPayload(updatedPayload);
    }

    const assistantMessage: ChatMessage = {
      id: `assistant-${Date.now()}`,
      role: 'aurora',
      text: inferAuroraResponse(prompt, text, nextData),
      timestamp: nowLabel(),
    };

    const cadenceMultiplier =
      activePersonality.cadence === 'criteriosa'
        ? 1.25
        : activePersonality.cadence === 'investigativa'
          ? 1.2
          : activePersonality.cadence === 'metódica'
            ? 1.15
            : 1;
    const responseDelayMs = Math.max(350, Math.min(1400, Math.round((280 + text.length * 10) * cadenceMultiplier)));
    await new Promise((resolve) => window.setTimeout(resolve, responseDelayMs));

    setInterviewData(nextData);
    syncWorkpadFromInterview(nextData);
    setChatMessages((prev) => [...prev, userMessage, assistantMessage]);
    setChatInput('');
    setIsAssistantTyping(false);
    setPresenceState('responding');
    window.setTimeout(() => {
      setPresenceState('idle');
    }, 1200);
  };

  const handleExecute = async () => {
    if (!endpoint) return;
    setLoading(true);
    setError(null);
    setResult(null);
    setElapsedMs(0);
    setWorkpadBlocks((prev) =>
      prev.map((block) => {
        if (
          block.type === 'technical' ||
          block.type === 'documental' ||
          block.type === 'visual' ||
          block.type === 'training' ||
          block.type === 'investigative'
        ) {
          return {
            ...block,
            metadata: {
              ...block.metadata,
              phase: 'em validação',
              needsConfirmation: true,
              lastUpdate: nowLabel(),
            },
          };
        }
        return block;
      }),
    );

    try {
      let retriesObserved = 0;
      const response = await callAuroraEndpoint(endpoint, payload, {
        timeoutMs: 60000,
        maxRetries: 2,
        onTelemetry: (event) => {
          if (event.phase === 'retry') retriesObserved += 1;
          appendTelemetry({ type: 'api', ...event });
        },
      });
      setElapsedMs(response.elapsedMs);
      setRuntimeState((prev) => ({
        ...prev,
        lastEndpoint: endpoint.key,
        lastStatus: response.status,
        retries: retriesObserved,
        lastError: response.status === 200 ? '' : `HTTP ${response.status}`,
        lastSuccessAt: response.status === 200 ? nowLabel() : prev.lastSuccessAt,
      }));

      if (response.status !== 200) {
        setError(`HTTP ${response.status}: ${JSON.stringify(response.data)}`);
      } else {
        setResult(response.data);
        setWorkpadBlocks((prev) =>
          prev.map((block) => {
            if (
              block.type === 'technical' ||
              block.type === 'documental' ||
              block.type === 'visual' ||
              block.type === 'training' ||
              block.type === 'investigative'
            ) {
              const baseCompletion = typeof block.metadata?.completion === 'number' ? block.metadata.completion : interviewCompletion;
              return {
                ...block,
                metadata: {
                  ...block.metadata,
                  phase: phaseFromProgress(baseCompletion, baseCompletion > 0, false, true),
                  completion: clampNumber(Math.round(baseCompletion + 8), 0, 100),
                  confidence: clampNumber(Math.round((block.metadata?.confidence || 60) + 7), 0, 100),
                  needsConfirmation: false,
                  lastUpdate: nowLabel(),
                },
              };
            }
            return block;
          }),
        );
        // Adicionar resultado ao workpad
        const resultBlock: WorkpadBlock = {
          id: `result-${Date.now()}`,
          type: 'code',
          content: JSON.stringify(response.data, null, 2),
          metadata: { endpoint: endpoint.label, elapsed: response.elapsedMs },
        };
        setWorkpadBlocks((prev) => [...prev, resultBlock]);
      }
    } catch (err) {
      setError((err instanceof Error ? err.message : String(err)) || 'Erro desconhecido');
      setRuntimeState((prev) => ({
        ...prev,
        lastEndpoint: endpoint.key,
        lastStatus: 0,
        lastError: (err instanceof Error ? err.message : String(err)) || 'Erro desconhecido',
      }));
    } finally {
      setLoading(false);
    }
  };

  const quickPrompts = activePersonality.quickPrompts;

  const handleInvestigativeFileUpload = async (files: FileList | null) => {
    const file = files?.[0];
    if (!file) return;

    // Limite de 10 MB para arquivos contextuais
    const MAX_FILE_SIZE_MB = 10;
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      setError(`Arquivo muito grande. Limite: ${MAX_FILE_SIZE_MB} MB. Tamanho atual: ${(file.size / (1024 * 1024)).toFixed(1)} MB.`);
      return;
    }

    setInvestigativeFileName(file.name);
    setPresenceState('processing');
    const parsed = await parseOperationalFile(file);
    const nextNote = `Arquivo ${file.name} triado. Risco ${parsed.riskScore}%.`;
    setHumanReviewNote(nextNote);
    setOperationalMemory((prev) => {
      const nextMemory: OperationalMemoryState = {
        ...prev,
        activeContext: prev.activeContext || `Ingestão de ${file.name}`,
        openInvestigation: `Arquivo em análise: ${file.name}`,
        pendingNotes: parsed.signals.slice(0, 4),
        priorDecisions: [nextNote, ...prev.priorDecisions].slice(0, 4),
        refinements: [`Triagem automática executada para ${file.name}`, ...prev.refinements].slice(0, 4),
        currentPhase: parsed.riskScore >= 70 ? 'validação' : 'triagem',
      };
      setTimelineEvents(createTimelineEvents(nextMemory));
      safeWriteStorage('aurora-operational-memory', JSON.stringify(nextMemory));
      return nextMemory;
    });

    setWorkpadBlocks((prev) =>
      prev.map((block) => {
        if (block.type !== 'investigative') return block;
        return {
          ...block,
          content: `Arquivo ${file.name} ingestado. ${parsed.summary}`,
          metadata: {
            ...block.metadata,
            phase: parsed.riskScore >= 70 ? 'em validação' : 'triando' as unknown as BlockLifecycleState,
            completion: clampNumber(Math.round((block.metadata?.completion || 0) + 15), 0, 100),
            confidence: clampNumber(Math.round(parsed.riskScore), 0, 100),
            needsConfirmation: parsed.riskScore >= 45,
            triage: parsed.riskScore >= 85 ? 'revisão humana' : parsed.riskScore >= 60 ? 'triado' : 'em triagem',
            fileNames: [file.name],
            findings: parsed.lines.slice(0, 4),
            signals: parsed.signals,
            riskScore: parsed.riskScore,
            decisionNotes: [parsed.summary],
            lastUpdate: nowLabel(),
          },
        };
      }),
    );
    setChatMessages((prev) => [
      ...prev,
      {
        id: `file-${Date.now()}`,
        role: 'aurora',
        text: `Arquivo ${file.name} recebido e triado. ${parsed.signals[0] || 'Sem sinais críticos imediatos.'}`,
        timestamp: nowLabel(),
      },
    ]);
    setPresenceState('idle');
  };

  const handleHumanCheckpoint = (checkpoint: string) => {
    const note = `${checkpoint} em ${nowLabel()}`;
    setHumanReviewNote(note);

    // Cada checkpoint tem consequências operacionais distintas
    const isApproval = checkpoint === 'Aprovar leitura';
    const isAdjustRequest = checkpoint === 'Solicitar ajuste';
    const isJustification = checkpoint === 'Registrar justificativa';

    const nextPhase = isApproval ? 'aprovação' : isAdjustRequest ? 'refinamento' : 'validação';

    setOperationalMemory((prev) => {
      const nextMemory: OperationalMemoryState = {
        ...prev,
        priorDecisions: [note, ...prev.priorDecisions].slice(0, 4),
        currentPhase: nextPhase,
        refinements: isAdjustRequest
          ? [`Ajuste solicitado às ${nowLabel()}`, ...prev.refinements].slice(0, 4)
          : prev.refinements,
      };
      setTimelineEvents(createTimelineEvents(nextMemory));
      safeWriteStorage('aurora-operational-memory', JSON.stringify(nextMemory));
      return nextMemory;
    });

    setWorkpadBlocks((prev) =>
      prev.map((block) => {
        if (
          block.type === 'technical' ||
          block.type === 'documental' ||
          block.type === 'visual' ||
          block.type === 'training' ||
          block.type === 'investigative'
        ) {
          const blockPhase = isApproval
            ? 'aprovado operacionalmente'
            : isAdjustRequest
              ? 'refinando'
              : 'em validação';
          return {
            ...block,
            metadata: {
              ...block.metadata,
              phase: blockPhase,
              needsConfirmation: isAdjustRequest,
              validated: isApproval,
              inconsistent: isAdjustRequest,
              confidence: isApproval
                ? clampNumber(Math.round((block.metadata?.confidence || 70) + 4), 0, 100)
                : block.metadata?.confidence,
              decisionNotes: [note, ...(block.metadata?.decisionNotes || [])].slice(0, 4),
            },
          };
        }
        return block;
      }),
    );

    // Injetar mensagem Aurora reagindo ao checkpoint
    const checkpointReply = isApproval
      ? `Aprovação registrada operacionalmente. Análise consolidada como referência para o fluxo.`
      : isAdjustRequest
        ? `Ajuste solicitado. Blocos marcados para refinamento — próxima interação irá reprocessar os pontos críticos.`
        : `Justificativa registrada no histórico de decisões. Referência mantida para auditoria.`;

    setChatMessages((prev) => [
      ...prev,
      {
        id: `checkpoint-${Date.now()}`,
        role: 'aurora' as const,
        text: checkpointReply,
        timestamp: nowLabel(),
      },
    ]);

    // Registrar como justificativa (apenas para checkpoint específico)
    if (isJustification) {
      setChatInput('Registrar: ');
    }
  };

  return (
    <div
      className="min-h-screen relative overflow-hidden"
      style={{
        background: 'radial-gradient(circle at 15% 25%, #1a0f35 0%, #0c0615 35%, #050308 100%)',
      }}
    >
      {/* Grid animado de fundo */}
      <div
        className="pointer-events-none absolute inset-0 opacity-20"
        style={{
          backgroundImage: 'linear-gradient(rgba(106,61,216,0.15) 1px, transparent 1px), linear-gradient(90deg, rgba(0,212,255,0.1) 1px, transparent 1px)',
          backgroundSize: '52px 52px',
        }}
      />

      {/* HEADER EXECUTIVO */}
      <div
        className="sticky top-0 z-40 px-6 py-4 border-b backdrop-blur-xl"
        style={{ borderColor: 'rgba(106,61,216,0.4)', background: 'rgba(8,10,20,0.85)' }}
      >
        <div className="max-w-[1600px] mx-auto flex items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <div
              ref={orbMain}
              className="w-8 h-8 rounded-full flex-shrink-0"
              style={{
                background: 'radial-gradient(circle at 30% 30%, #e8a4ff, #6a3dd8 50%, #1a0a3f)',
                boxShadow: '0 0 20px rgba(106,61,216,0.4), inset -1px -1px 4px rgba(0,0,0,0.3)',
              }}
            />
            <div>
              <p className="text-white text-sm font-bold tracking-wide" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                Aurora Sentinel
                <span style={{ color: '#00d4ff', marginLeft: '8px' }}>OPERAÇÃO</span>
              </p>
              <p className="text-[10px] text-[#8a96c2]">Entrevista + Workpad + Execução Integrada</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowTechnicalPanel((p) => !p)}
              className="px-3 py-1.5 text-xs font-medium rounded-full border transition"
              style={{
                color: showTechnicalPanel ? '#00d4ff' : '#9ca8cc',
                borderColor: showTechnicalPanel ? 'rgba(0,212,255,0.4)' : 'rgba(106,118,175,0.3)',
                background: showTechnicalPanel ? 'rgba(0,212,255,0.1)' : 'rgba(12,14,25,0.6)',
              }}
            >
              Painel Técnico
            </button>

            <div
              className="px-3 py-1.5 text-xs rounded-full border"
              style={{
                borderColor: 'rgba(34,204,136,0.35)',
                color: '#22cc88',
                background: 'rgba(34,204,136,0.08)',
              }}
            >
              Isolada ✓
            </div>

            <div
              className="px-3 py-1.5 text-xs rounded-full border"
              title={bridgeState.error || `HTTP ${bridgeState.statusCode} • ${bridgeState.latencyMs}ms • ${bridgeState.checkedAt}`}
              style={{
                borderColor: bridgeState.online ? 'rgba(34,204,136,0.35)' : 'rgba(255,100,120,0.35)',
                color: bridgeState.online ? '#22cc88' : '#ff6478',
                background: bridgeState.online ? 'rgba(34,204,136,0.08)' : 'rgba(255,100,120,0.08)',
              }}
            >
              {bridgeState.online ? `API online ${bridgeState.latencyMs}ms` : 'API offline'}
            </div>
          </div>
        </div>
      </div>

      {/* CONTEÚDO PRINCIPAL */}
      <div className="relative z-10 max-w-[1600px] mx-auto px-6 pt-8 pb-16">
        {/* BANNER OPERACIONAL */}
        <div className="flex items-end gap-5 mb-10">
          <div
            ref={orbAccent}
            className="w-24 h-24 rounded-full flex-shrink-0"
            style={{
              background: 'radial-gradient(circle at 35% 35%, #d4a8ff, #6a3dd8 55%, #0d0022)',
              boxShadow: '0 0 24px rgba(0,212,255,0.25), inset 2px 2px 8px rgba(0,212,255,0.1)',
            }}
          />
          <div>
            <h1
              className="text-5xl font-bold text-white leading-tight"
              style={{
                fontFamily: "'Space Grotesk', sans-serif",
                background: 'linear-gradient(135deg, #d4a8ff 0%, #6a3dd8 35%, #3388ff 65%, #00d4ff)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              Aurora Operacional
            </h1>
            <p className="text-[#a8b4d9] mt-1 text-sm">A IA conduz a entrevista. O workpad produz em paralelo. Documentos nascem de respostas.</p>
          </div>
        </div>

        {/* SELETOR DE MÓDULOS */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
          {availableModules.map((mod) => (
            <button
              key={mod.id}
              onClick={() => setActiveModule(mod.id)}
              className="text-left p-3.5 rounded-lg border transition-all duration-200"
              style={{
                background:
                  activeModule === mod.id
                    ? 'linear-gradient(145deg, rgba(106,61,216,0.28), rgba(0,212,255,0.1))'
                    : 'rgba(14,16,28,0.7)',
                borderColor:
                  activeModule === mod.id
                    ? 'rgba(106,61,216,0.55)'
                    : 'rgba(106,118,175,0.25)',
                boxShadow:
                  activeModule === mod.id
                    ? '0 0 24px rgba(106,61,216,0.2)'
                    : 'none',
              }}
            >
              <div className="text-2xl mb-1.5">{mod.icon}</div>
              <div className="text-white text-xs font-semibold leading-tight">{mod.label}</div>
              <div className="text-[10px] text-[#8a96c2] mt-0.5 line-clamp-2">{mod.desc}</div>
            </button>
          ))}
        </div>

        {/* FAIXA DE PRESENÇA OPERACIONAL */}
        <div
          className="rounded-2xl border mb-6 p-4"
          style={{
            background: 'linear-gradient(120deg, rgba(14,17,30,0.9), rgba(21,25,44,0.88))',
            borderColor: 'rgba(106,118,175,0.28)',
          }}
        >
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <p className="text-[10px] uppercase tracking-[0.15em] text-[#7f8eb5]">Estado Vivo</p>
              <p className="text-sm text-[#dce5ff] font-semibold">{presenceLabelByState[presenceState]}</p>
              <p className="text-[11px] text-[#9aabd5] mt-0.5">{ambientMessage}</p>
              <p className="text-[10px] text-[#8a96c2] mt-1">
                {activePersonality.modeName} • Cadência {activePersonality.cadence} • Profundidade {activePersonality.depth}
              </p>
              <div className="mt-2 flex items-center gap-3">
                <span
                  className="text-[10px] px-2 py-0.5 rounded-full"
                  style={{
                    color:
                      operationalTensionLevel === 'bloqueado' ? '#ff6478'
                      : operationalTensionLevel === 'crítico' ? '#f3c969'
                      : operationalTensionLevel === 'ativo' ? '#00d4ff'
                      : '#22cc88',
                    background:
                      operationalTensionLevel === 'bloqueado' ? 'rgba(255, 100, 120, 0.16)'
                      : operationalTensionLevel === 'crítico' ? 'rgba(243, 201, 105, 0.12)'
                      : operationalTensionLevel === 'ativo' ? 'rgba(0, 212, 255, 0.08)'
                      : 'rgba(34, 204, 136, 0.08)',
                    border:
                      operationalTensionLevel === 'bloqueado' ? '1px solid rgba(255, 100, 120, 0.35)'
                      : operationalTensionLevel === 'crítico' ? '1px solid rgba(243, 201, 105, 0.25)'
                      : operationalTensionLevel === 'ativo' ? '1px solid rgba(0, 212, 255, 0.18)'
                      : '1px solid rgba(34, 204, 136, 0.2)',
                  }}
                >
                  Tensão: {operationalTensionLevel.charAt(0).toUpperCase() + operationalTensionLevel.slice(1)}
                </span>
                {macroPhase && macroPhase !== 'coleta operacional' && (
                  <span
                    className="text-[10px] px-2 py-0.5 rounded-full"
                    style={{
                      color: '#a9b7dc',
                      background: 'rgba(169, 183, 220, 0.1)',
                      border: '1px solid rgba(169, 183, 220, 0.2)',
                    }}
                  >
                    Fase: {macroPhase}
                  </span>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 md:flex md:items-center md:gap-6">
              <div>
                <p className="text-[10px] uppercase tracking-widest text-[#7f8eb5]">Uptime</p>
                <p className="text-sm text-white font-semibold">{sessionUptimeLabel}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest text-[#7f8eb5]">Cobertura</p>
                <p className="text-sm text-white font-semibold">{interviewCompletion}%</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest text-[#7f8eb5]">Runtime</p>
                <p className="text-sm text-white font-semibold">
                  {runtimeState.lastStatus > 0 ? `HTTP ${runtimeState.lastStatus}` : 'Sem execução'}
                </p>
              </div>
            </div>
          </div>
          <div className="mt-3 h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(106,118,175,0.2)' }}>
            <div
              className="h-full transition-all duration-500"
              style={{
                width: `${interviewCompletion}%`,
                background: 'linear-gradient(90deg, #6a3dd8, #3388ff, #00d4ff)',
              }}
            />
          </div>
          <div className="mt-2 flex items-center gap-2">
            {[0, 1, 2, 3].map((pulse) => (
              <span
                key={pulse}
                className="h-1 rounded-full"
                style={{
                  width: presenceState === 'processing' ? '26px' : '18px',
                  background: pulse % 2 === 0 ? 'rgba(0,212,255,0.55)' : 'rgba(106,61,216,0.55)',
                  animation: `signalPulse 1.8s ease-in-out ${pulse * 0.18}s infinite`,
                }}
              />
            ))}
            <span className="text-[10px] text-[#8a96c2]">Atividade operacional contínua</span>
          </div>
        </div>

        {/* LAYOUT PRINCIPAL: Workpad + Chat Acoplável */}
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-5">
          {/* WORKPAD EDITOR PROFISSIONAL */}
          <div
            className="xl:col-span-7 rounded-2xl border p-6 min-h-[700px] flex flex-col"
            style={{
              background: 'rgba(10,12,20,0.92)',
              borderColor: 'rgba(106,118,175,0.3)',
            }}
          >
            <div className="flex h-full flex-col">
              <div className="flex items-center justify-between gap-3 mb-4 pb-4 border-b" style={{ borderColor: 'rgba(106,118,175,0.2)' }}>
                <div>
                  <p className="text-xs uppercase tracking-widest text-[#7f8eb5]">Espaço de Trabalho</p>
                  <h2 className="text-lg text-white font-semibold mt-0.5">Workpad Operacional em Tempo Real</h2>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleExecute}
                    disabled={loading || !endpoint}
                    className="px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-50"
                    style={{
                      background: loading ? 'rgba(106,61,216,0.3)' : 'linear-gradient(135deg, #6a3dd8, #3388ff)',
                    }}
                  >
                    {loading ? 'Processando...' : 'Executar API'}
                  </button>
                </div>
              </div>

              {/* BLOCOS DO WORKPAD */}
              <div className="flex-1 overflow-y-auto space-y-3 pr-2">
                <div className="rounded-xl p-3 border" style={{ background: 'rgba(8,10,18,0.8)', borderColor: 'rgba(106,118,175,0.22)' }}>
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <p className="text-[10px] uppercase tracking-widest text-[#7f8eb5]">Presença Visual da Aurora</p>
                    <span className="text-[10px] text-[#8a96c2]">{operationalPriorityLabel}</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    {auroraPresenceHints.map((hint, index) => (
                      <div
                        key={hint}
                        className="rounded-lg px-3 py-2 border"
                        style={{
                          background: index === 0 ? 'rgba(0,212,255,0.07)' : 'rgba(10,12,20,0.55)',
                          borderColor: index === 0 ? 'rgba(0,212,255,0.22)' : 'rgba(106,118,175,0.2)',
                        }}
                      >
                        <p className="text-[9px] uppercase tracking-wider text-[#8a96c2]">Sinal {index + 1}</p>
                        <p className="text-[11px] text-[#dce5ff] mt-1">{hint}</p>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 flex items-center gap-2 text-[10px] text-[#8a96c2]">
                    <span className="w-2 h-2 rounded-full bg-[#00d4ff]" style={{ boxShadow: '0 0 10px rgba(0,212,255,0.4)' }} />
                    Aurora acompanhando o ambiente sem ocupar a tela
                  </div>
                </div>

                <div className="rounded-xl p-3 border" style={{ background: 'rgba(8,10,18,0.8)', borderColor: 'rgba(106,118,175,0.22)' }}>
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <p className="text-[10px] uppercase tracking-widest text-[#7f8eb5]">Linha do Tempo Operacional</p>
                    <span className="text-[10px] text-[#8a96c2]">{operationalMemory.currentPhase}</span>
                  </div>
                  <div className="space-y-2">
                    {timelineEvents.map((event) => (
                      <div key={event.id} className="flex gap-3 items-start">
                        <div
                          className="mt-1 w-2.5 h-2.5 rounded-full flex-shrink-0"
                          style={{
                            background: event.active ? '#00d4ff' : 'rgba(138,150,194,0.45)',
                            boxShadow: event.active ? '0 0 12px rgba(0,212,255,0.45)' : 'none',
                          }}
                        />
                        <div className="flex-1 rounded-lg px-3 py-2" style={{ background: event.active ? 'rgba(0,212,255,0.07)' : 'rgba(10,12,20,0.55)' }}>
                          <div className="flex items-center justify-between gap-2">
                            <p className="text-[11px] font-semibold text-[#dce5ff]">{event.label}</p>
                            <span className="text-[9px] text-[#7f8eb5] uppercase tracking-wider">{event.at}</span>
                          </div>
                          <p className="text-[10px] text-[#a9b7dc] mt-1">{event.detail}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div
                  className="rounded-xl p-3 border"
                  style={{
                    background: 'rgba(9,12,22,0.82)',
                    borderColor: 'rgba(106,118,175,0.24)',
                  }}
                >
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <p className="text-[10px] uppercase tracking-widest text-[#7f8eb5]">Painel de Consciência Operacional</p>
                    <span
                      className="text-[10px] px-2 py-0.5 rounded-full"
                      style={{
                        color: refinementColor,
                        background: `${refinementColor}22`,
                      }}
                    >
                      {workpadRefinementState}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-[11px]">
                    <div className="rounded-lg p-2" style={{ background: 'rgba(106,61,216,0.08)' }}>
                      <p className="text-[#8a96c2] uppercase tracking-wider text-[9px]">O que já entendi</p>
                      <p className="text-[#dce5ff] mt-1">{operationalSnapshot.understood.length ? operationalSnapshot.understood.join(' • ') : 'Aguardando dados iniciais'}</p>
                    </div>
                    <div className="rounded-lg p-2" style={{ background: 'rgba(0,212,255,0.08)' }}>
                      <p className="text-[#8a96c2] uppercase tracking-wider text-[9px]">O que ainda preciso</p>
                      <p className="text-[#dce5ff] mt-1">{operationalSnapshot.needs.length ? operationalSnapshot.needs.join(' • ') : 'Coleta essencial concluída'}</p>
                    </div>
                    <div className="rounded-lg p-2" style={{ background: 'rgba(34,204,136,0.08)' }}>
                      <p className="text-[#8a96c2] uppercase tracking-wider text-[9px]">O que já estruturei</p>
                      <p className="text-[#dce5ff] mt-1">{operationalSnapshot.structured} blocos em construção ativa</p>
                    </div>
                    <div className="rounded-lg p-2" style={{ background: 'rgba(243,201,105,0.08)' }}>
                      <p className="text-[#8a96c2] uppercase tracking-wider text-[9px]">Nível de completude</p>
                      <p className="text-[#dce5ff] mt-1">{operationalSnapshot.completion}% pronto para entrega</p>
                    </div>
                  </div>
                </div>

                {workpadBlocks.map((block) => {
                  if (block.type === 'heading') {
                    const level = (block.metadata?.level as number) || 1;
                    const sizes = { 1: 'text-xl', 2: 'text-base', 3: 'text-sm' };
                    return (
                      <div key={block.id} className={`${sizes[level as keyof typeof sizes] || 'text-base'} font-bold text-[#d0dcff] mt-2 mb-1`}>
                        {block.content}
                      </div>
                    );
                  }
                  if (block.type === 'spacer') {
                    return <div key={block.id} className="h-2" />;
                  }
                  if (block.type === 'checklist') {
                    const items = block.content.split('|');
                    return (
                      <div key={block.id} className="space-y-1.5">
                        {items.map((item, i) => (
                          <label key={i} className="flex items-center gap-2 cursor-pointer group">
                            <input
                              type="checkbox"
                              className="w-4 h-4 rounded border-[1.5px]"
                              style={{
                                borderColor: 'rgba(106,61,216,0.4)',
                                accentColor: '#6a3dd8',
                              }}
                            />
                            <span className="text-sm text-[#c5d3f0] group-hover:text-[#dce5ff]">{item.trim()}</span>
                          </label>
                        ))}
                      </div>
                    );
                  }
                  if (block.type === 'code') {
                    return (
                      <pre
                        key={block.id}
                        className="text-xs whitespace-pre-wrap break-words rounded-lg p-3 overflow-auto max-h-[200px]"
                        style={{
                          background: 'rgba(8,10,18,0.85)',
                          color: '#9db3ff',
                          border: '1px solid rgba(106,118,175,0.2)',
                        }}
                      >
                        {block.content}
                      </pre>
                    );
                  }
                  if (
                    block.type === 'technical' ||
                    block.type === 'documental' ||
                    block.type === 'visual' ||
                    block.type === 'training' ||
                    block.type === 'investigative'
                  ) {
                    const phase = block.metadata?.phase || 'rascunho';
                    const completion = typeof block.metadata?.completion === 'number' ? block.metadata.completion : 0;
                    const confidence = typeof block.metadata?.confidence === 'number' ? block.metadata.confidence : 0;
                    const pending = block.metadata?.pending || [];
                    const needsConfirmation = Boolean(block.metadata?.needsConfirmation);
                    const blockLabelByType: Record<WorkpadBlockType, string> = {
                      text: 'Texto',
                      heading: 'Cabeçalho',
                      list: 'Lista',
                      table: 'Tabela',
                      code: 'Código',
                      checklist: 'Checklist',
                      spacer: 'Espaço',
                      technical: 'Bloco Técnico',
                      documental: 'Bloco Documental',
                      visual: 'Bloco Visual',
                      training: 'Bloco Treinamento/PPT',
                      investigative: 'Bloco Investigativo',
                    };
                    const style = phaseStyles[phase];

                    return (
                      <div
                        key={block.id}
                        className="rounded-xl p-3 border transition-all duration-300"
                        style={{
                          background: 'rgba(10,13,24,0.75)',
                          borderColor: 'rgba(106,118,175,0.25)',
                          animation: phase === 'em validação' ? 'operationalSweep 2.4s ease-in-out infinite' : undefined,
                          order: block.type === 'visual' && operationalPriorityLabel !== 'coleta ativa' ? -1 : 'unset',
                        }}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div>
                            <p className="text-[10px] uppercase tracking-widest text-[#7f8eb5]">{blockLabelByType[block.type]}</p>
                            <p className="text-sm text-[#dce5ff] font-semibold">{activePersonality.modeName}</p>
                          </div>
                          <div className="flex items-center gap-1">
                            {/* Indicador de Gargalo */}
                            {detectedBottlenecks.some((bn) => bn.blockId === block.id && bn.severity === 'critical') && (
                              <span
                                title="Gargalo crítico detectado"
                                className="text-[10px] px-2 py-0.5 rounded-full"
                                style={{
                                  color: '#ff6478',
                                  background: 'rgba(255, 100, 120, 0.16)',
                                  border: '1px solid rgba(255, 100, 120, 0.35)',
                                }}
                              >
                                ⚠️ Crítico
                              </span>
                            )}
                            {detectedBottlenecks.some((bn) => bn.blockId === block.id && bn.severity === 'high') && (
                              <span
                                title="Gargalo de alta prioridade"
                                className="text-[10px] px-2 py-0.5 rounded-full"
                                style={{
                                  color: '#f3c969',
                                  background: 'rgba(243, 201, 105, 0.12)',
                                  border: '1px solid rgba(243, 201, 105, 0.25)',
                                }}
                              >
                                ⚡ Alto
                              </span>
                            )}
                            <span
                              className="text-[10px] px-2 py-0.5 rounded-full"
                              style={{
                                color: style.text,
                                background: style.background,
                                border: style.border,
                              }}
                            >
                              {phase}
                            </span>
                          </div>
                        </div>

                        <p className="mt-2 text-[12px] text-[#cdd9fb] leading-relaxed">{block.content}</p>

                        <div className="mt-2 h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(106,118,175,0.2)' }}>
                          <div
                            className="h-full transition-all duration-500"
                            style={{
                              width: `${completion}%`,
                              background: 'linear-gradient(90deg, #6a3dd8, #3388ff, #00d4ff)',
                            }}
                          />
                        </div>

                        <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-2 text-[10px]">
                          <div className="rounded-md p-2" style={{ background: 'rgba(106,61,216,0.08)' }}>
                            <p className="text-[#8a96c2] uppercase tracking-wider">Completude</p>
                            <p className="text-[#dce5ff] mt-1">{completion}%</p>
                          </div>
                          <div className="rounded-md p-2" style={{ background: 'rgba(0,212,255,0.08)' }}>
                            <p className="text-[#8a96c2] uppercase tracking-wider">Confiança operacional</p>
                            <p className="text-[#dce5ff] mt-1">{confidence}%</p>
                          </div>
                          <div className="rounded-md p-2" style={{ background: 'rgba(243,201,105,0.08)' }}>
                            <p className="text-[#8a96c2] uppercase tracking-wider">Confirmação</p>
                            <p className="text-[#dce5ff] mt-1">{needsConfirmation ? 'Necessária' : 'Confirmada'}</p>
                          </div>
                        </div>

                        <div className="mt-2 rounded-md p-2" style={{ background: 'rgba(8,10,18,0.72)', border: '1px solid rgba(106,118,175,0.2)' }}>
                          <p className="text-[9px] uppercase tracking-wider text-[#8a96c2]">Pendências</p>
                          <p className="text-[11px] text-[#dce5ff] mt-1">{pending.length ? pending.join(' • ') : 'Sem pendências relevantes'}</p>
                        </div>

                        {block.type === 'documental' && block.metadata?.attachments?.length ? (
                          <p className="text-[10px] text-[#a9b7dc] mt-2">Anexos: {block.metadata.attachments.join(' • ')}</p>
                        ) : null}
                        {block.type === 'visual' && (
                          <div className="mt-3 rounded-xl border overflow-hidden" style={{ background: 'rgba(6,8,16,0.85)', borderColor: 'rgba(0,212,255,0.18)' }}>
                            {/* Cabeçalho do fluxograma */}
                            <div className="flex items-center justify-between px-3 py-2 border-b" style={{ borderColor: 'rgba(106,118,175,0.18)' }}>
                              <div className="flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full" style={{ background: '#00d4ff', boxShadow: '0 0 8px rgba(0,212,255,0.55)' }} />
                                <p className="text-[9px] uppercase tracking-widest text-[#7f8eb5]">Fluxograma Operacional Editável</p>
                              </div>
                              <span className="text-[9px] text-[#8a96c2]">{flowNodes.length} nós • drag para mover</span>
                            </div>

                            {/* SVG de fluxograma */}
                            <div style={{ position: 'relative', width: '100%', height: '280px' }}>
                              <svg
                                ref={flowSvgRef}
                                width="100%"
                                height="280"
                                viewBox="0 0 640 280"
                                preserveAspectRatio="xMidYMid meet"
                                style={{ cursor: draggingNodeId ? 'grabbing' : 'default', display: 'block' }}
                                onMouseMove={handleFlowMouseMove}
                                onMouseUp={handleFlowMouseUp}
                                onMouseLeave={handleFlowMouseUp}
                              >
                                {/* Definições: setas e filtros */}
                                <defs>
                                  <marker id="arrowBlue" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
                                    <path d="M0,0 L0,6 L8,3 z" fill="rgba(0,212,255,0.7)" />
                                  </marker>
                                  <marker id="arrowRed" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
                                    <path d="M0,0 L0,6 L8,3 z" fill="rgba(255,100,120,0.7)" />
                                  </marker>
                                  <filter id="nodeGlow">
                                    <feGaussianBlur in="SourceGraphic" stdDeviation="2" result="blur" />
                                    <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
                                  </filter>
                                </defs>

                                {/* Arestas/Conexões */}
                                {flowEdges.map((edge) => {
                                  const fromNode = flowNodes.find((n) => n.id === edge.from);
                                  const toNode = flowNodes.find((n) => n.id === edge.to);
                                  if (!fromNode || !toNode) return null;
                                  const mx = (fromNode.x + toNode.x) / 2;
                                  const my = (fromNode.y + toNode.y) / 2 - 24;
                                  const strokeColor = edge.critical ? 'rgba(255,100,120,0.6)' : 'rgba(0,212,255,0.35)';
                                  const markerId = edge.critical ? 'url(#arrowRed)' : 'url(#arrowBlue)';
                                  return (
                                    <g key={edge.id}>
                                      <path
                                        d={`M${fromNode.x},${fromNode.y} Q${mx},${my} ${toNode.x},${toNode.y}`}
                                        fill="none"
                                        stroke={strokeColor}
                                        strokeWidth={edge.critical ? 2 : 1.5}
                                        strokeDasharray={edge.critical ? 'none' : '5,3'}
                                        markerEnd={markerId}
                                      />
                                      {edge.label && (
                                        <text
                                          x={mx}
                                          y={my - 5}
                                          textAnchor="middle"
                                          fill="rgba(138,150,194,0.75)"
                                          fontSize="8"
                                          fontFamily="'Space Grotesk', sans-serif"
                                        >
                                          {edge.label}
                                        </text>
                                      )}
                                    </g>
                                  );
                                })}

                                {/* Arestas de Dependência Operacional (roxo/pontilhado) */}
                                {blockDependencies.map((dep) => {
                                  const fromBlock = workpadBlocks.find((b) => b.id === dep.from);
                                  const toBlock = workpadBlocks.find((b) => b.id === dep.to);
                                  if (!fromBlock || !toBlock) return null;
                                  
                                  // Tentar mapear blocos para nós (aproximação)
                                  const fromNode = flowNodes.find((n) => n.label.includes(dep.from.slice(-3)) || flowNodes[0]);
                                  const toNode = flowNodes.find((n) => n.label.includes(dep.to.slice(-3)) || flowNodes[flowNodes.length - 1]);
                                  if (!fromNode || !toNode) return null;

                                  const depColor = 
                                    dep.type === 'conflict' ? 'rgba(255, 100, 120, 0.35)'
                                    : dep.type === 'priority' ? 'rgba(243, 201, 105, 0.35)'
                                    : 'rgba(170, 100, 230, 0.35)'; // prerequisite, enhancement

                                  const mx = (fromNode.x + toNode.x) / 2;
                                  const my = (fromNode.y + toNode.y) / 2 + 24;

                                  return (
                                    <g key={`dep-${dep.id}`} opacity="0.6">
                                      <path
                                        d={`M${fromNode.x},${fromNode.y + 20} Q${mx},${my} ${toNode.x},${toNode.y - 20}`}
                                        fill="none"
                                        stroke={depColor}
                                        strokeWidth="1"
                                        strokeDasharray="4,4"
                                      />
                                      {dep.type === 'priority' && (
                                        <circle cx={mx} cy={my} r="3" fill="rgba(243, 201, 105, 0.7)" />
                                      )}
                                    </g>
                                  );
                                })}

                                {/* Nós do fluxograma */}
                                {flowNodes.map((node) => {
                                  const nodeColor =
                                    node.type === 'entrada' ? '#6a3dd8'
                                    : node.type === 'risco' ? (node.riskScore > 65 ? '#ff6478' : '#f3c969')
                                    : node.type === 'entrega' ? '#22cc88'
                                    : node.type === 'validação' ? '#00d4ff'
                                    : '#3388ff';
                                  const borderColor =
                                    node.inconsistent ? 'rgba(255,100,120,0.8)'
                                    : node.validated ? 'rgba(34,204,136,0.7)'
                                    : node.priority === 'alta' ? `${nodeColor}cc`
                                    : `${nodeColor}66`;
                                  const bgOpacity = node.completion > 50 ? 0.18 : 0.09;
                                  const nodeW = 100;
                                  const nodeH = 40;
                                  const rx = node.type === 'decisão' ? 8 : 6;

                                  return (
                                    <g
                                      key={node.id}
                                      transform={`translate(${node.x - nodeW / 2},${node.y - nodeH / 2})`}
                                      style={{ cursor: 'grab' }}
                                      onMouseDown={(e) => handleNodeMouseDown(e, node.id)}
                                      filter="url(#nodeGlow)"
                                    >
                                      {/* Anel externo de prioridade */}
                                      {node.priority === 'alta' && (
                                        <rect
                                          x="-3" y="-3" width={nodeW + 6} height={nodeH + 6}
                                          rx={rx + 2}
                                          fill="none"
                                          stroke={nodeColor}
                                          strokeWidth="0.8"
                                          strokeOpacity="0.4"
                                          strokeDasharray="3,3"
                                        />
                                      )}
                                      {/* Corpo do nó */}
                                      <rect
                                        x="0" y="0" width={nodeW} height={nodeH}
                                        rx={rx}
                                        fill={`rgba(${node.type === 'entrada' ? '106,61,216' : node.type === 'risco' && node.riskScore > 65 ? '255,100,120' : node.type === 'entrega' ? '34,204,136' : node.type === 'validação' ? '0,212,255' : '51,136,255'},${bgOpacity})`}
                                        stroke={borderColor}
                                        strokeWidth="1.2"
                                      />
                                      {/* Barra de completude na base do nó */}
                                      <rect
                                        x="4" y={nodeH - 5} width={Math.max(0, (nodeW - 8) * node.completion / 100)} height="3"
                                        rx="1.5"
                                        fill={nodeColor}
                                        fillOpacity="0.55"
                                      />
                                      {/* Label do nó */}
                                      <text
                                        x={nodeW / 2} y={nodeH / 2 - 4}
                                        textAnchor="middle"
                                        fill="#dce5ff"
                                        fontSize="9"
                                        fontWeight="600"
                                        fontFamily="'Space Grotesk', sans-serif"
                                      >
                                        {node.label.length > 18 ? node.label.slice(0, 18) + '…' : node.label}
                                      </text>
                                      {/* Tipo */}
                                      <text
                                        x={nodeW / 2} y={nodeH / 2 + 6}
                                        textAnchor="middle"
                                        fill={nodeColor}
                                        fontSize="7"
                                        fontFamily="'Space Grotesk', sans-serif"
                                      >
                                        {node.type}
                                      </text>
                                      {/* Badge de inconsistência */}
                                      {node.inconsistent && (
                                        <circle cx={nodeW - 4} cy="4" r="4" fill="rgba(255,100,120,0.9)" />
                                      )}
                                      {/* Badge de validação */}
                                      {node.validated && !node.inconsistent && (
                                        <circle cx={nodeW - 4} cy="4" r="4" fill="rgba(34,204,136,0.85)" />
                                      )}
                                    </g>
                                  );
                                })}

                                {/* Sugestão avançada da Aurora - contextual e orquestrada */}
                                {auroraAdvancedSuggestion && (
                                  <g>
                                    <rect x="4" y="254" width="572" height="22" rx="4"
                                      fill={
                                        operationalTensionLevel === 'bloqueado' ? 'rgba(255, 100, 120, 0.06)'
                                        : operationalTensionLevel === 'crítico' ? 'rgba(243, 201, 105, 0.06)'
                                        : 'rgba(0, 212, 255, 0.05)'
                                      }
                                      stroke={
                                        operationalTensionLevel === 'bloqueado' ? 'rgba(255, 100, 120, 0.15)'
                                        : operationalTensionLevel === 'crítico' ? 'rgba(243, 201, 105, 0.15)'
                                        : 'rgba(0, 212, 255, 0.12)'
                                      }
                                      strokeWidth="0.8"
                                    />
                                    <text x="14" y="269"
                                      fill={
                                        operationalTensionLevel === 'bloqueado' ? 'rgba(255, 100, 120, 0.65)'
                                        : operationalTensionLevel === 'crítico' ? 'rgba(243, 201, 105, 0.65)'
                                        : 'rgba(0, 212, 255, 0.55)'
                                      }
                                      fontSize="8.5" fontFamily="'Space Grotesk', sans-serif"
                                    >
                                      {auroraAdvancedSuggestion}
                                    </text>
                                    {/* Botão Aplicar */}
                                    <rect x="578" y="255" width="28" height="20" rx="4"
                                      fill="rgba(34,204,136,0.12)" stroke="rgba(34,204,136,0.3)" strokeWidth="0.8"
                                      style={{ cursor: 'pointer' }}
                                      onClick={() => {
                                        setChatInput(auroraAdvancedSuggestion);
                                        setTimeout(() => void handleSendChat(), 50);
                                        setAuroraAdvancedSuggestion('');
                                      }}
                                    />
                                    <text x="592" y="269" textAnchor="middle" fill="rgba(34,204,136,0.85)" fontSize="8" fontFamily="sans-serif" style={{ cursor: 'pointer' }}
                                      onClick={() => {
                                        setChatInput(auroraAdvancedSuggestion);
                                        setTimeout(() => void handleSendChat(), 50);
                                        setAuroraAdvancedSuggestion('');
                                      }}
                                    >✓</text>
                                    {/* Botão Dispensar */}
                                    <rect x="608" y="255" width="28" height="20" rx="4"
                                      fill="rgba(106,118,175,0.08)" stroke="rgba(106,118,175,0.2)" strokeWidth="0.8"
                                      style={{ cursor: 'pointer' }}
                                      onClick={() => setAuroraAdvancedSuggestion('')}
                                    />
                                    <text x="622" y="269" textAnchor="middle" fill="rgba(106,118,175,0.7)" fontSize="8" fontFamily="sans-serif" style={{ cursor: 'pointer' }}
                                      onClick={() => setAuroraAdvancedSuggestion('')}
                                    >✕</text>
                                  </g>
                                )}
                              </svg>
                            </div>

                            {/* Thumbnails operacionais */}
                            <div className="px-3 pb-3 border-t pt-2" style={{ borderColor: 'rgba(106,118,175,0.15)' }}>
                              <p className="text-[9px] uppercase tracking-widest text-[#7f8eb5] mb-2">Snapshots Operacionais</p>
                              <div className="grid grid-cols-4 gap-2">
                                {workpadBlocks
                                  .filter((b) => ['technical', 'documental', 'training', 'investigative'].includes(b.type))
                                  .slice(0, 4)
                                  .map((b) => {
                                    const thumbPhase = b.metadata?.phase || 'rascunho';
                                    const thumbCompl = b.metadata?.completion || 0;
                                    const thumbConf = b.metadata?.confidence || 0;
                                    const thumbColor =
                                      thumbPhase === 'aprovado operacionalmente' ? '#22cc88'
                                      : thumbPhase === 'em validação' ? '#00d4ff'
                                      : thumbPhase === 'refinando' ? '#f3c969'
                                      : '#8a96c2';
                                    const thumbIconByType: Record<string, string> = {
                                      technical: '⚙', documental: '📄', training: '🎓', investigative: '🔍',
                                    };
                                    return (
                                      <div
                                        key={b.id}
                                        className="rounded-lg p-2 border flex flex-col gap-1"
                                        style={{ background: 'rgba(10,12,20,0.8)', borderColor: `${thumbColor}44` }}
                                      >
                                        <span className="text-[14px]">{thumbIconByType[b.type] || '▪'}</span>
                                        <p className="text-[8px] text-[#dce5ff] leading-tight line-clamp-2">{b.content.slice(0, 40)}</p>
                                        <div className="h-1 rounded-full overflow-hidden" style={{ background: 'rgba(106,118,175,0.2)' }}>
                                          <div className="h-full" style={{ width: `${thumbCompl}%`, background: thumbColor }} />
                                        </div>
                                        <p style={{ color: thumbColor, fontSize: '8px' }}>{thumbConf}% conf.</p>
                                      </div>
                                    );
                                  })}
                              </div>
                            </div>
                          </div>
                        )}
                        {block.type === 'training' && block.metadata?.slides?.length ? (
                          <p className="text-[10px] text-[#a9b7dc] mt-2">Slides vivos: {block.metadata.slides.join(' • ')}</p>
                        ) : null}
                        {block.type === 'investigative' && block.metadata?.signals?.length ? (
                          <p className="text-[10px] text-[#a9b7dc] mt-2">Sinais investigativos: {block.metadata.signals.join(' • ')}</p>
                        ) : null}
                        {block.type === 'investigative' ? (
                          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
                            <label className="rounded-lg p-2 border" style={{ background: 'rgba(8,10,18,0.75)', borderColor: 'rgba(106,118,175,0.2)' }}>
                              <p className="text-[9px] uppercase tracking-wider text-[#8a96c2]">Upload contextual</p>
                              <input
                                type="file"
                                accept=".pdf,.txt,.md,.csv,.json,.xml,.png,.jpg,.jpeg,.webp,.doc,.docx"
                                onChange={(e) => void handleInvestigativeFileUpload(e.target.files)}
                                className="mt-2 w-full text-[10px] text-[#dce5ff]"
                              />
                            </label>
                            <div className="rounded-lg p-2 border" style={{ background: 'rgba(8,10,18,0.75)', borderColor: 'rgba(106,118,175,0.2)' }}>
                              <p className="text-[9px] uppercase tracking-wider text-[#8a96c2]">Revisão humana</p>
                              <div className="flex flex-wrap gap-1 mt-2">
                                {['Aprovar leitura', 'Solicitar ajuste', 'Registrar justificativa'].map((checkpoint) => (
                                  <button
                                    key={checkpoint}
                                    onClick={() => handleHumanCheckpoint(checkpoint)}
                                    className="text-[10px] px-2 py-1 rounded-full transition"
                                    style={{ background: 'rgba(106,61,216,0.16)', border: '1px solid rgba(106,61,216,0.25)', color: '#dce5ff' }}
                                  >
                                    {checkpoint}
                                  </button>
                                ))}
                              </div>
                              <p className="text-[10px] text-[#a9b7dc] mt-2">{humanReviewNote || 'Checkpoint de decisão aguardando revisão.'}</p>
                            </div>
                          </div>
                        ) : null}
                        {block.type === 'training' ? (
                          <div className="mt-3 rounded-lg p-2 border" style={{ background: 'rgba(8,10,18,0.72)', borderColor: 'rgba(106,118,175,0.18)' }}>
                            <p className="text-[9px] uppercase tracking-wider text-[#8a96c2]">Preview educacional</p>
                            <div className="mt-2 space-y-1">
                              {(block.metadata?.slides || []).slice(0, 4).map((slide, index) => (
                                <div key={slide} className="flex items-center gap-2">
                                  <span className="text-[9px] px-2 py-0.5 rounded-full" style={{ background: index === 0 ? 'rgba(0,212,255,0.08)' : 'rgba(106,61,216,0.08)', color: '#dce5ff' }}>
                                    {index + 1}
                                  </span>
                                  <span className="text-[10px] text-[#cdd9fb]">{slide}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : null}
                      </div>
                    );
                  }
                  const labelText = typeof block.metadata?.label === 'string' ? block.metadata.label : null;
                  const placeholderText = typeof block.metadata?.placeholder === 'string' ? block.metadata.placeholder : '';
                  return (
                    <div
                      key={block.id}
                      className="rounded-lg p-3 text-sm"
                      style={{
                        background:
                          block.metadata?.status === 'complete'
                            ? 'rgba(34,204,136,0.08)'
                            : block.metadata?.status === 'pending'
                              ? 'rgba(243,201,105,0.08)'
                              : 'rgba(10,12,20,0.6)',
                        border:
                          block.metadata?.status === 'complete'
                            ? '1px solid rgba(34,204,136,0.25)'
                            : block.metadata?.status === 'pending'
                              ? '1px solid rgba(243,201,105,0.25)'
                              : '1px solid rgba(106,118,175,0.2)',
                        color: '#d0dcff',
                      }}
                    >
                      {labelText ? (
                        <p className="text-xs uppercase tracking-wider text-[#8a96c2] mb-1">{labelText}</p>
                      ) : null}
                      <div>{block.content || placeholderText || '(Vazio)'}</div>
                    </div>
                  );
                })}
              </div>

              {/* PARÂMETROS DO PAYLOAD */}
              {Object.keys(payload).length > 0 && (
                <div className="mt-4 pt-4 border-t" style={{ borderColor: 'rgba(106,118,175,0.2)' }}>
                  <p className="text-xs uppercase tracking-widest text-[#7f8eb5] mb-2">Parâmetros da API</p>
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(payload).map(([key, value]) => (
                      <label key={key} className="rounded-lg p-2" style={{ background: 'rgba(8,10,18,0.8)', border: '1px solid rgba(106,118,175,0.22)' }}>
                        <span className="text-[10px] uppercase text-[#7f8eb5]">{key}</span>
                        <input
                          value={String(value ?? '')}
                          onChange={(e) => handlePayloadChange(key, e.target.value)}
                          className="mt-0.5 w-full bg-transparent text-xs text-[#dce5ff] focus:outline-none"
                        />
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* RESULTADO DA API */}
              {result !== null && (
                <div className="mt-4 pt-4 border-t" style={{ borderColor: 'rgba(106,118,175,0.2)' }}>
                  <p className="text-xs uppercase tracking-widest text-[#7f8eb5] mb-2">Resultado ({elapsedMs}ms)</p>
                  <pre className="text-xs text-[#9db3ff] max-h-[120px] overflow-auto">{JSON.stringify(result, null, 2)}</pre>
                </div>
              )}
              {error && (
                <div className="mt-4 pt-4 border-t" style={{ borderColor: 'rgba(106,118,175,0.2)' }}>
                  <p className="text-xs uppercase tracking-widest text-[#7f8eb5] mb-2">Erro da API</p>
                  <p className="text-xs text-[#ff6b7a]">{error}</p>
                </div>
              )}
            </div>
          </div>

          {/* PAINEL DIREITO: Técnico ou Rádios */}
          {showTechnicalPanel ? (
            <div
              className="xl:col-span-5 rounded-2xl border p-6 flex flex-col gap-5 min-h-[700px]"
              style={{
                background: 'rgba(12,15,26,0.92)',
                borderColor: 'rgba(106,118,175,0.3)',
              }}
            >
              {/* ENTREVISTA E CONTEXTO */}
              <div className="flex-1 rounded-xl p-4" style={{ background: 'rgba(8,10,18,0.8)', border: '1px solid rgba(106,118,175,0.24)' }}>
                <p className="text-xs uppercase tracking-widest text-[#7f8eb5]">Entrevista Estruturada</p>
                <h3 className="text-sm text-white font-semibold mt-1">
                  {firstMissingPrompt
                    ? `Próxima pergunta:`
                    : 'Entrevista concluída'}
                </h3>
                {firstMissingPrompt && (
                  <p className="text-xs text-[#c5d3f0] mt-2 italic">{firstMissingPrompt.question}</p>
                )}
                <p className="text-xs text-[#7f8eb5] mt-3">
                  Coletados: <span className="text-[#22cc88]">{collectedCount}</span>
                  /
                  <span className="text-[#f3c969]">{interviewFlow.length}</span>
                </p>
              </div>

              {/* COBERTURA DE CONTEXTO */}
              <div className="flex-1 rounded-xl p-4" style={{ background: 'rgba(8,10,18,0.8)', border: '1px solid rgba(106,118,175,0.24)' }}>
                <p className="text-xs uppercase tracking-widest text-[#7f8eb5]">Cobertura Técnica</p>
                <div className="mt-3 space-y-1.5">
                  {interviewFlow.map((entry) => {
                    const filled = Boolean((interviewData[entry.field] || '').trim());
                    return (
                      <div key={entry.field} className="flex items-center justify-between text-xs">
                        <span style={{ color: filled ? '#dce5ff' : '#8794bb' }}>{entry.label}</span>
                        <span
                          className="px-2 py-0.5 rounded-full text-[10px]"
                          style={{
                            color: filled ? '#22cc88' : '#f3c969',
                            background: filled ? 'rgba(34,204,136,0.12)' : 'rgba(243,201,105,0.12)',
                          }}
                        >
                          {filled ? '✓' : '○'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* CONSULTA TÉCNICA NR */}
              <div className="rounded-xl p-4" style={{ background: 'rgba(8,10,18,0.8)', border: '1px solid rgba(106,118,175,0.24)' }}>
                <p className="text-xs uppercase tracking-widest text-[#7f8eb5]">Central Técnica (NR)</p>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {['NR-01', 'NR-05', 'NR-06', 'NR-10', 'NR-12', 'NR-35'].map((nr) => (
                    <button
                      key={nr}
                      onClick={() => {
                        setChatInput(`Consultar ${nr}: quais são as principais obrigações e requisitos?`);
                        setTimeout(() => void handleSendChat(), 50);
                      }}
                      className="text-[10px] px-2 py-1 rounded-full border transition hover:opacity-80 active:scale-95"
                      style={{
                        borderColor: 'rgba(106,61,216,0.3)',
                        color: '#d0dbff',
                        background: 'rgba(106,61,216,0.1)',
                        cursor: 'pointer',
                      }}
                    >
                      {nr}
                    </button>
                  ))}
                </div>
                <p className="text-[10px] text-[#8a96c2] mt-2">Clique para consulta contextual imediata</p>
              </div>
            </div>
          ) : (
            /* PAINEL DE RÁDIOS E FEEDS */
            <div
              className="xl:col-span-5 rounded-2xl border p-6 space-y-4"
              style={{
                background: 'rgba(12,15,26,0.92)',
                borderColor: 'rgba(106,118,175,0.3)',
              }}
            >
              {/* RÁDIOS */}
              <div>
                <p className="text-xs uppercase tracking-widest text-[#7f8eb5]">Ambientação Sonora</p>
                <div className="mt-2 space-y-1.5">
                  {RADIO_SCENES.map((scene) => (
                    <button
                      key={scene.id}
                      onClick={() => setActiveScene(scene.id)}
                      className="w-full text-left rounded-lg p-2 border transition"
                      style={{
                        borderColor:
                          activeScene === scene.id
                            ? 'rgba(0,212,255,0.4)'
                            : 'rgba(106,118,175,0.2)',
                        background:
                          activeScene === scene.id
                            ? 'rgba(0,212,255,0.08)'
                            : 'rgba(8,10,18,0.8)',
                      }}
                    >
                      <p className="text-xs text-[#dce5ff] font-medium">{scene.label}</p>
                      <p className="text-[10px] text-[#8a96c2]">{scene.desc}</p>
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => setSoundEnabled((p) => !p)}
                  className="mt-3 w-full py-2 rounded-lg text-xs font-semibold transition"
                  style={{
                    color: soundEnabled ? '#0a111f' : '#dce5ff',
                    background: soundEnabled ? 'linear-gradient(135deg, #00d4ff, #3388ff)' : 'linear-gradient(135deg, #6a3dd8, #3388ff)',
                  }}
                >
                  {soundEnabled ? '🔊 Trilha Ativa' : '🔇 Ativar Trilha'}
                </button>
              </div>

              {/* FEEDS */}
              <div className="border-t pt-4" style={{ borderColor: 'rgba(106,118,175,0.2)' }}>
                <p className="text-xs uppercase tracking-widest text-[#7f8eb5]">Feeds Técnicos</p>
                <div className="mt-2 space-y-1.5 max-h-[250px] overflow-y-auto">
                  {FEED_SOURCES.map((feed) => (
                    <button
                      key={feed.url}
                      onClick={() => window.open(feed.url, '_blank', 'noopener,noreferrer')}
                      className="w-full text-left rounded-lg p-2 text-[10px] hover:opacity-80 transition"
                      style={{
                        background: 'rgba(8,10,18,0.8)',
                        border: '1px solid rgba(106,118,175,0.2)',
                        cursor: 'pointer',
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <p className="text-[11px] text-[#dce5ff] font-medium truncate">{feed.name}</p>
                        <span className="text-[9px] text-[#6a70a0] ml-1 shrink-0">↗</span>
                      </div>
                      <p className="text-[9px] text-[#8a96c2]">{feed.category}</p>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* CHAT FLUTUANTE/ACOPLÁVEL (AURORA CLIPPY) */}
      <ChatAssistant
        chatDock={chatDock}
        setChatDock={setChatDock}
        chatMessages={chatMessages}
        chatInput={chatInput}
        setChatInput={setChatInput}
        onSend={handleSendChat}
        quickPrompts={quickPrompts}
        chatScrollRef={chatScrollRef}
        chatWindowRef={chatWindowRef}
        presenceState={presenceState}
        isAssistantTyping={isAssistantTyping}
      />

      <style>{`
        @keyframes auraPulse {
          0%, 100% { transform: scale(1); opacity: 0.95; }
          50% { transform: scale(1.02); opacity: 0.88; }
        }
        @keyframes signalPulse {
          0%, 100% { opacity: 0.35; transform: translateY(0); }
          50% { opacity: 0.95; transform: translateY(-1px); }
        }
        @keyframes operationalSweep {
          0%, 100% { box-shadow: 0 0 0 rgba(0,212,255,0); }
          50% { box-shadow: 0 0 18px rgba(0,212,255,0.16); }
        }
      `}</style>
    </div>
  );
}

// Componente do Chat Flutuante/Acoplável (Aurora Clippy)
function ChatAssistant({
  chatDock,
  setChatDock,
  chatMessages,
  chatInput,
  setChatInput,
  onSend,
  quickPrompts,
  chatScrollRef,
  chatWindowRef,
  presenceState,
  isAssistantTyping,
}: {
  chatDock: ChatDockState;
  setChatDock: (state: ChatDockState) => void;
  chatMessages: ChatMessage[];
  chatInput: string;
  setChatInput: (text: string) => void;
  onSend: () => void;
  quickPrompts: string[];
  chatScrollRef: React.RefObject<HTMLDivElement | null>;
  chatWindowRef: React.RefObject<HTMLDivElement | null>;
  presenceState: AuroraPresenceState;
  isAssistantTyping: boolean;
}) {
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef<{ startX: number; startY: number; offsetX: number; offsetY: number }>({
    startX: 0,
    startY: 0,
    offsetX: 0,
    offsetY: 0,
  });

  const handleMouseDown = (e: React.MouseEvent) => {
    if (chatDock !== 'floating') return;
    setIsDragging(true);
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      offsetX: dragOffset.x,
      offsetY: dragOffset.y,
    };
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !dragRef.current) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    // Limitar drag dentro dos bounds da viewport
    const chatW = 480;
    const chatH = 600;
    const maxX = window.innerWidth - chatW - 16;
    const maxY = window.innerHeight - chatH - 16;
    setDragOffset({
      x: Math.max(-window.innerWidth + chatW + 16, Math.min(maxX, dragRef.current.offsetX + dx)),
      y: Math.max(-window.innerHeight + chatH + 16, Math.min(maxY, dragRef.current.offsetY + dy)),
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  if (chatDock === 'minimized') {
    return (
      <button
        onClick={() => setChatDock('floating')}
        className="fixed bottom-6 right-6 z-50 px-4 py-2 rounded-full text-sm font-semibold text-white"
        style={{
          background: 'linear-gradient(135deg, #6a3dd8, #00d4ff)',
          boxShadow: '0 8px 28px rgba(106,61,216,0.35)',
        }}
      >
        Aurora Assistiva 💬
      </button>
    );
  }

  const baseClasses =
    'fixed z-50 rounded-2xl border overflow-hidden flex flex-col';
  const windowStyle: React.CSSProperties = {
    background: 'rgba(11,13,24,0.96)',
    borderColor: 'rgba(106,61,216,0.45)',
    boxShadow: '0 20px 50px rgba(0,0,0,0.5), 0 0 40px rgba(106,61,216,0.15)',
    backdropFilter: 'blur(10px)',
  };

  const positionStyle: React.CSSProperties =
    chatDock === 'floating'
      ? {
          width: 'min(92vw, 480px)',
          height: '600px',
          bottom: '24px',
          right: '24px',
          transform: `translate(${dragOffset.x}px, ${dragOffset.y}px)`,
          cursor: isDragging ? 'grabbing' : 'auto',
        }
      : chatDock === 'docked-right'
        ? {
            width: '420px',
            height: '100vh',
            top: '0',
            right: '0',
            borderRadius: '0',
          }
        : chatDock === 'docked-left'
          ? {
              width: '420px',
              height: '100vh',
              top: '0',
              left: '0',
              borderRadius: '0',
            }
          : {};

  return (
    <div
      ref={chatWindowRef}
      className={baseClasses}
      style={{ ...windowStyle, ...positionStyle }}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      {/* HEADER COM DRAG HANDLE */}
      <div
        className="px-4 py-3 border-b flex items-center justify-between cursor-grab active:cursor-grabbing select-none"
        style={{ borderColor: 'rgba(106,118,175,0.28)' }}
        onMouseDown={handleMouseDown}
      >
        <div className="flex items-center gap-2">
          <span
            className="w-2.5 h-2.5 rounded-full"
            style={{
              background:
                presenceState === 'processing'
                  ? '#f3c969'
                  : presenceState === 'responding'
                    ? '#00d4ff'
                    : '#22cc88',
              boxShadow:
                presenceState === 'processing'
                  ? '0 0 12px rgba(243,201,105,0.65)'
                  : presenceState === 'responding'
                    ? '0 0 12px rgba(0,212,255,0.65)'
                    : '0 0 12px rgba(34,204,136,0.65)',
            }}
          />
          <span className="text-sm text-white font-semibold">Aurora Assistiva</span>
        </div>

        <div className="flex items-center gap-2">
          {chatDock === 'floating' && (
            <>
              <button
                onClick={() => setChatDock('docked-right')}
                className="text-xs text-[#8491b8] hover:text-[#dce5ff] transition"
                title="Acoplar à direita"
              >
                ⊟
              </button>
              <button
                onClick={() => setChatDock('minimized')}
                className="text-xs text-[#8491b8] hover:text-[#dce5ff] transition"
              >
                _
              </button>
            </>
          )}
          {(chatDock === 'docked-right' || chatDock === 'docked-left') && (
            <button
              onClick={() => setChatDock('floating')}
              className="text-xs text-[#8491b8] hover:text-[#dce5ff] transition"
            >
              ◻
            </button>
          )}
        </div>
      </div>

      {/* MENSAGENS */}
      <div
        ref={chatScrollRef}
        className="flex-1 overflow-y-auto px-4 py-3 space-y-3"
        style={{
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(106,118,175,0.3) rgba(8,10,18,0.8)',
        }}
      >
        {chatMessages.map((msg) => (
          <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div
              className="max-w-[85%] rounded-xl px-3 py-2"
              style={{
                background:
                  msg.role === 'user'
                    ? 'rgba(51,136,255,0.18)'
                    : 'rgba(106,61,216,0.18)',
                border:
                  msg.role === 'user'
                    ? '1px solid rgba(51,136,255,0.3)'
                    : '1px solid rgba(106,61,216,0.3)',
              }}
            >
              <p className="text-sm text-[#e4ebff] whitespace-pre-wrap leading-relaxed">{msg.text}</p>
              <p className="text-[9px] text-[#8491b8] mt-1">{msg.timestamp}</p>
            </div>
          </div>
        ))}
        {isAssistantTyping && (
          <div className="flex justify-start">
            <div
              className="max-w-[85%] rounded-xl px-3 py-2"
              style={{
                background: 'rgba(106,61,216,0.18)',
                border: '1px solid rgba(106,61,216,0.3)',
              }}
            >
              <p className="text-sm text-[#cfdcff]">Aurora está estruturando a resposta...</p>
            </div>
          </div>
        )}
      </div>

      {/* QUICK PROMPTS */}
      <div className="px-4 py-2 flex flex-wrap gap-1.5 border-t" style={{ borderColor: 'rgba(106,118,175,0.2)' }}>
        {quickPrompts.map((prompt) => (
          <button
            key={prompt}
            onClick={() => setChatInput(prompt)}
            className="text-[10px] px-2 py-1 rounded-full transition"
            style={{
              color: '#d5e0ff',
              background: 'rgba(106,61,216,0.18)',
              border: '1px solid rgba(106,61,216,0.25)',
            }}
          >
            {prompt}
          </button>
        ))}
      </div>

      {/* INPUT */}
      <div className="px-4 pb-3 pt-2 border-t space-y-2" style={{ borderColor: 'rgba(106,118,175,0.2)' }}>
        <div className="flex gap-2">
          <input
            value={chatInput}
            onChange={(e) => setChatInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                onSend();
              }
            }}
            placeholder="Responda naturalmente..."
            className="flex-1 px-3 py-2 rounded-lg text-sm focus:outline-none"
            style={{
              background: 'rgba(8,10,18,0.92)',
              color: '#e4ebff',
              border: '1px solid rgba(106,118,175,0.28)',
            }}
          />
          <button
            onClick={onSend}
            className="px-3 py-2 rounded-lg text-sm font-semibold text-white"
            style={{
              background: 'linear-gradient(135deg, #6a3dd8, #3388ff)',
            }}
          >
            ↑
          </button>
        </div>
      </div>
    </div>
  );
}
